import { S } from '../core/game';
import type { Decor, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shadow, shade, sun, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Pose } from './sprites';

/**
 * Prop drawers of the hlcity package (Hualien city, Dongdamen Night Market, Pine Garden and Ji'an):
 * objects and decor with the `hc_` prefix.
 *
 * Outdoor props are redrawn every frame, so every big or detailed piece is painted once into an
 * offscreen canvas (one for day, one for night) and blitted afterwards; only small animated bits
 * (bulbs, steam, the singer's lights, birds) are drawn live. Landmarks taller than 90 px come as
 * several decor slices (`variant` = slice, `h` = full height), see `big()` in maps/hlcity.ts.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
const isNight = () => sun.alpha < 0.8;
const H = (h: number) => h * 60;
const marketOpen = () => S().time >= H(17);

// ---------------------------------------------------------------------------
// Small helpers (same look as props4 / propsHualien)
// ---------------------------------------------------------------------------

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

/** Vertical text, one character per line. */
function vtxt(c: Ctx, s: string, x: number, y: number, size: number, col: string, gap = 1.05, shadowCol?: string): void {
  [...s].forEach((ch, i) => txt(c, ch, x, y + i * size * gap, size, col, 900, shadowCol));
}

function sign(c: Ctx, text: string, cx: number, y: number, w: number, bg: string, fg = '#fff6dc', size = 8): void {
  block(c, cx - w / 2, y, w, size + 3, bg, shade(bg, 0.25), shade(bg, -0.3));
  txt(c, text, cx, y + size * 0.74, size, fg, 900, 'rgba(0,0,0,0.35)');
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

function steam(c: Ctx, x: number, y: number, t: number, n = 3, a = 0.35): void {
  for (let i = 0; i < n; i++) {
    const k = (t * 0.6 + i / n) % 1;
    ellipse(c, `rgba(255,255,255,${a * (1 - k)})`, x + Math.sin(t * 2 + i * 2) * 2, y - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
}

/** Deterministic 0..1 from two numbers. */
function rnd(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Amis-style pattern band: diamonds and zig-zags in red, black, white and yellow. */
function amisBand(c: Ctx, x: number, y: number, w: number, h: number, kind = 0): void {
  const bg = ['#c8202a', '#1b1d24', '#f4f0e6'][kind % 3];
  const fg = ['#f4f0e6', '#ffd34e', '#c8202a'][kind % 3];
  rect(c, bg, x, y, w, h);
  const s = h;
  for (let i = 0; i < w; i += s) {
    const cx = x + i + s / 2, cy = y + h / 2;
    if (kind % 2 === 0) poly(c, fg, [cx, y + 0.5, x + i + s - 0.5, cy, cx, y + h - 0.5, x + i + 0.5, cy]);
    else poly(c, fg, [x + i, y + h, cx, y, x + i + s, y + h]);
    if (h >= 4) ellipse(c, bg, cx, cy, s * 0.12, s * 0.12);
  }
  rect(c, 'rgba(0,0,0,0.2)', x, y + h - 0.4, w, 0.4);
}

// ---------------------------------------------------------------------------
// Offscreen cache: paint once, blit every frame
// ---------------------------------------------------------------------------

const ART = new Map<string, HTMLCanvasElement>();

/**
 * Blit a cached painting whose local origin (0,0) is its bottom-centre at (x, y). The painting spans
 * x ∈ [-w/2, w/2], y ∈ [-h, 8]; `y0..y1` (local) selects a horizontal band (for slices).
 */
function blit(c: Ctx, key: string, x: number, y: number, w: number, h: number, paint: (c: Ctx) => void, y0 = -h, y1 = 8): void {
  const s = Math.min(3, Math.max(1, Math.ceil(Math.abs(c.getTransform().a) - 0.05)));
  const k = `${key}@${s}`;
  let cv = ART.get(k);
  if (!cv) {
    cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil((h + 8) * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(w / 2, h);
    paint(cc);
    ART.set(k, cv);
  }
  const sy = (y0 + h) * s, sh = (y1 - y0) * s;
  if (sh <= 0) return;
  c.drawImage(cv, 0, sy, cv.width, Math.min(sh, cv.height - sy), x - w / 2, y + y0, w, Math.min(y1 - y0, cv.height / s - (y0 + h)));
}

const SLICE = 80;
/** Draw slice `d.variant` of a big landmark (see `big()` in the map). Returns the full bottom y. */
function landmark(c: Ctx, d: Decor, key: string, paint: (c: Ctx, night: boolean) => void): number {
  const k = d.variant ?? 0, h = d.h ?? 90, w = d.w ?? 64;
  const n = Math.max(1, Math.ceil((h - 90) / SLICE) + 1);
  const bottom = d.y + SLICE * k;
  const y1 = k === 0 ? 8 : -SLICE * k;
  const y0 = k === n - 1 ? -h : -SLICE * (k + 1);
  const night = isNight();
  blit(c, `${key}${night ? 'N' : 'D'}`, d.x, bottom, w, h, (cc) => paint(cc, night), y0, y1);
  return bottom;
}

/** A cached, non-sliced decor painting (variant-aware). */
function art(c: Ctx, d: Decor, key: string, w: number, h: number, paint: (c: Ctx, night: boolean) => void, nightAware = true): void {
  const night = nightAware && isNight();
  blit(c, `${key}:${d.variant ?? 0}${night ? 'N' : 'D'}`, d.x, d.y, w, h, (cc) => paint(cc, night));
}

/** Same, for an object rect (anchored at the rect's bottom-centre). */
function artObj(c: Ctx, r: Rect, key: string, w: number, h: number, paint: (c: Ctx, night: boolean) => void): void {
  const night = isNight();
  blit(c, `${key}${night ? 'N' : 'D'}`, r.x + r.w / 2, r.y + r.h, w, h, (cc) => paint(cc, night));
}

/** Window pane: glassy by day, warm light at night (some rooms dark). */
function pane(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, seed: number, frame = '#e8e4da'): void {
  rect(c, shade(frame, -0.35), x - 0.5, y - 0.5, w + 1, h + 1);
  if (night) {
    const lit = rnd(seed, 7) < 0.62;
    rect(c, lit ? (rnd(seed, 9) < 0.3 ? '#ffe9b0' : '#ffd88a') : '#2a3448', x, y, w, h);
    if (lit) rect(c, 'rgba(255,255,255,0.35)', x, y, w, 0.8);
  } else {
    vgrad(c, x, y, w, h, '#a8cfe4', '#5d86a4');
    rect(c, 'rgba(255,255,255,0.45)', x + 0.6, y + 0.6, Math.max(0.6, w * 0.25), h - 1.2);
  }
}

// ---------------------------------------------------------------------------
// Hualien Station
// ---------------------------------------------------------------------------

function station(c: Ctx, night: boolean): void {
  const W = 320, L = -W / 2, R = W / 2;
  shadow(c, 0, 0, W / 2, 4, 0.3);
  // Roof top surface seen from above (rows 4–6): pale metal panels receding north.
  vgrad(c, L + 6, -128, W - 12, 40, '#cfd6da', '#e8ecee');
  for (let x = L + 12; x < R - 8; x += 12) rect(c, 'rgba(90,110,120,0.18)', x, -128, 0.6, 40);
  rect(c, '#b8c0c6', L + 6, -128, W - 12, 1.2);
  // Skylight strip and rooftop plant boxes.
  rect(c, night ? '#ffe4a8' : '#8fb8d0', -70, -120, 140, 4);
  rect(c, 'rgba(255,255,255,0.4)', -70, -120, 140, 0.8);
  // The roof's front: a white sweeping canopy whose top edge rises in three peaks like the
  // Central Mountain Range behind the city.
  const top = (x: number) => -88 - 16 * Math.max(0, Math.cos(((x - 0) / W) * Math.PI * 3)) ** 2 - 6 * Math.cos((x / W) * Math.PI);
  c.fillStyle = '#f6f8f8';
  c.beginPath();
  c.moveTo(L - 6, -60);
  for (let x = L - 6; x <= R + 6; x += 4) c.lineTo(x, top(x));
  c.lineTo(R + 6, -60);
  c.closePath();
  c.fill();
  // Its shaded underside and a thin blue-grey edge.
  c.strokeStyle = '#7f98a8';
  c.lineWidth = 1.2;
  c.beginPath();
  for (let x = L - 6; x <= R + 6; x += 4) c.lineTo(x, top(x));
  c.stroke();
  for (let x = L; x <= R; x += 16) line(c, 'rgba(120,140,150,0.25)', 0.5, [x, top(x) + 2, x, -62]);
  // Fascia band with the Amis pattern.
  amisBand(c, L - 6, -64, W + 12, 5, 0);
  rect(c, '#2a3036', L - 6, -59, W + 12, 3);
  // Big station name on the canopy.
  txt(c, '花蓮車站', -10, -77, 18, '#c8202a', 900, 'rgba(0,0,0,0.25)');
  txt(c, 'HUALIEN STATION', -10, -66.5, 4.6, '#2a4a6a', 800);
  // Clock.
  ellipse(c, '#2a3036', 62, -76, 7.4, 7.4);
  ellipse(c, '#ffffff', 62, -76, 6.4, 6.4);
  line(c, '#2a3036', 0.8, [62, -76, 62, -80.6]);
  line(c, '#2a3036', 0.8, [62, -76, 65, -76]);
  // Railway emblem on the left.
  ellipse(c, '#2f5fa8', -88, -76, 6.5, 6.5);
  line(c, '#ffffff', 0.9, [-91.5, -71.5, -89, -80.5]);
  line(c, '#ffffff', 0.9, [-84.5, -71.5, -87, -80.5]);
  rect(c, '#ffffff', -91, -76.5, 6, 0.7);
  // The hall: glass curtain between slim white columns.
  const gy = -56;
  block(c, L + 2, gy, W - 4, 56, '#e9ecec', '#ffffff', '#c8cccc');
  for (let i = 0; i < 18; i++) {
    const x = L + 10 + i * 17.2;
    if (x > -40 && x < 26) continue; // the entrance portal stands here
    if (night) vgrad(c, x, gy + 6, 14, 44, '#ffe7b0', '#f2c070');
    else vgrad(c, x, gy + 6, 14, 44, '#b8d8ea', '#5a84a4');
    rect(c, 'rgba(255,255,255,0.35)', x + 1, gy + 7, 2.4, 40);
    rect(c, '#9aa6ae', x, gy + 27, 14, 0.8);
    if (night && i % 3 === 1) {
      // Travellers inside.
      ellipse(c, 'rgba(60,40,30,0.6)', x + 7, gy + 41, 2, 2);
      rect(c, 'rgba(60,40,30,0.6)', x + 5.4, gy + 43, 3.2, 7);
    }
  }
  // Columns wrapped in Amis patterns at the entrance, plain white elsewhere.
  for (let i = 0; i <= 18; i++) {
    const x = L + 8 + i * 17.2;
    block(c, x - 1, gy + 2, 3.4, 54, '#ffffff', '#ffffff', '#d4d8d8');
    if (Math.abs(x + 7) < 60) for (let k = 0; k < 5; k++) amisBand(c, x - 1, gy + 8 + k * 9, 3.4, 3.4, k);
  }
  // The base and a low step.
  rect(c, '#b8b2a6', L + 2, -3, W - 4, 3);
  rect(c, 'rgba(255,255,255,0.4)', L + 2, -3, W - 4, 0.6);
  // Information board and departures screen by the door.
  block(c, 34, -40, 30, 16, '#1b2430', '#2a3644');
  for (let k = 0; k < 4; k++) {
    rect(c, k % 2 ? '#ffb43a' : '#7fe08a', 36, -37 + k * 3.4, 10, 1.4);
    rect(c, '#e8ecef', 48, -37 + k * 3.4, 14, 1.4);
  }
  if (night) {
    c.globalCompositeOperation = 'lighter';
    ellipse(c, 'rgba(255,200,140,0.25)', -10, -77, 50, 12);
    c.globalCompositeOperation = 'source-over';
  }
}

/** The Taroko Express (太魯閣號) waiting at the platform behind the station. */
function train(c: Ctx, night: boolean): void {
  const W = 560, L = -W / 2;
  const cars = 3, cw = 178;
  for (let k = 0; k < cars; k++) {
    const x0 = L + 8 + k * (cw + 4);
    const nose = k === 0;
    const by = -2;
    // Body.
    c.fillStyle = '#f4f5f2';
    c.beginPath();
    if (nose) {
      c.moveTo(x0 + 26, by - 26);
      c.quadraticCurveTo(x0 + 4, by - 24, x0, by - 6);
      c.lineTo(x0, by);
    } else {
      c.moveTo(x0, by - 26);
      c.lineTo(x0, by);
    }
    c.lineTo(x0 + cw, by);
    c.lineTo(x0 + cw, by - 26);
    c.closePath();
    c.fill();
    c.strokeStyle = '#8a9096';
    c.lineWidth = 0.8;
    c.stroke();
    // Roof.
    rect(c, '#c8ccd0', x0 + (nose ? 22 : 0), by - 28, cw - (nose ? 22 : 0), 2.4);
    // Red-orange livery band and grey skirt.
    rect(c, '#e84a2a', x0 + (nose ? 4 : 0), by - 9, cw - (nose ? 4 : 0), 2.6);
    rect(c, '#f39a2a', x0 + (nose ? 6 : 0), by - 6.4, cw - (nose ? 6 : 0), 1.2);
    rect(c, '#5a6066', x0, by - 3, cw, 3);
    // Window band.
    for (let i = 0; i < 9; i++) {
      const wx = x0 + (nose ? 34 : 10) + i * 17;
      if (wx + 12 > x0 + cw - 4) break;
      rect(c, night ? (i % 3 ? '#ffe2a0' : '#ffd070') : '#2a3a4a', wx, by - 21, 12, 7);
      if (!night) rect(c, 'rgba(255,255,255,0.35)', wx + 1, by - 20.4, 3, 5.8);
    }
    // Doors.
    for (const dx of nose ? [cw - 14] : [6, cw - 14]) {
      rect(c, '#d8dcdf', x0 + dx, by - 23, 8, 20);
      rect(c, '#9aa0a6', x0 + dx + 3.8, by - 23, 0.5, 20);
      rect(c, night ? '#ffe2a0' : '#2a3a4a', x0 + dx + 1.4, by - 21, 5.2, 5);
    }
    if (nose) {
      // Cab windscreen and headlight.
      poly(c, '#1b2430', [x0 + 6, by - 13, x0 + 12, by - 22, x0 + 26, by - 23, x0 + 26, by - 13]);
      rect(c, 'rgba(255,255,255,0.3)', x0 + 16, by - 22, 3, 8);
      ellipse(c, night ? '#fff3c0' : '#e8e4da', x0 + 4, by - 5, 1.6, 1.2);
      txt(c, '太魯閣號', x0 + 90, by - 12, 4.6, '#c8202a', 900);
    }
    // Pantograph.
    if (k === 1) line(c, '#3a3e44', 0.7, [x0 + 80, by - 28, x0 + 90, by - 34, x0 + 100, by - 28]);
  }
  // Overhead wire.
  line(c, '#3a3e44', 0.5, [L, -34, L + W, -34]);
}

function amisPillar(c: Ctx, d: Decor): void {
  art(c, d, 'amisPillar', 20, 66, (c) => {
    shadow(c, 0, 0, 7, 2, 0.3, 30);
    block(c, -5, -56, 10, 56, '#f4f0e6', '#ffffff', '#d4ccbc');
    for (let k = 0; k < 7; k++) amisBand(c, -5, -54 + k * 7.6, 10, 4.4, k + (d.variant ?? 0));
    // Capital: a woven-hat shape with a ring of glass beads.
    poly(c, '#8a5a3a', [-8, -56, 8, -56, 4, -61, -4, -61]);
    rect(c, '#ffd34e', -8, -56.6, 16, 0.8);
    const beads = ['#ffd34e', '#e8473a', '#3aa0d9', '#5fbf5a', '#ffffff', '#ff8ac0'];
    for (let i = 0; i < 6; i++) ellipse(c, beads[i], -5 + i * 2, -58.4, 0.8, 0.8);
    ellipse(c, '#c8202a', 0, -63, 2.4, 2.4);
    block(c, -6, -2, 12, 2, '#b8b2a6');
  });
}

/** The station's Amis mural: dancers holding hands under the sun, mountains and sea. */
function mural(c: Ctx, r: Rect): void {
  artObj(c, r, 'mural', 84, 48, (c) => {
    shadow(c, 0, 0, 40, 3, 0.3, 20);
    block(c, -40, -44, 80, 42, '#e8dcc4', '#f6ecd8', '#b8a888');
    // Background: sky, mountains, sea.
    vgrad(c, -37, -41, 74, 22, '#ffcf7a', '#ffe9b8');
    ellipse(c, '#ff8a3a', -22, -33, 5, 5);
    poly(c, '#4f8a5a', [-37, -22, -20, -36, -6, -26, 8, -38, 26, -24, 37, -30, 37, -19, -37, -19]);
    rect(c, '#2f8fb8', -37, -19, 74, 4);
    // Dancers in a line, holding hands.
    for (let i = 0; i < 7; i++) {
      const x = -30 + i * 10;
      const man = i % 2 === 1;
      rect(c, '#1b1410', x - 1.6, -14, 1.2, 5);
      rect(c, '#1b1410', x + 0.4, -14, 1.2, 5);
      block(c, x - 3, -22, 6, 8, man ? '#1b1d24' : '#c8202a');
      rect(c, '#ffd34e', x - 3, -20.4, 6, 0.8);
      ellipse(c, '#a8683a', x, -24.4, 2, 2);
      rect(c, man ? '#ffd34e' : '#ffffff', x - 2.4, -26.6, 4.8, 1);
      line(c, '#a8683a', 0.9, [x + 3, -20, x + 7, -20]);
    }
    amisBand(c, -40, -44, 80, 3, 1);
    amisBand(c, -40, -5, 80, 3, 0);
    // Posts and plaque.
    for (const x of [-36, 33]) block(c, x, -4, 3, 4, '#6a4a2a');
    block(c, -12, -9, 24, 4, '#2a1810');
    txt(c, '阿美族 Amis', 0, -7, 2.8, '#ffd34e', 800);
  });
}

function taxi(c: Ctx, d: Decor): void {
  art(c, d, 'taxi', 32, 24, (c, night) => {
    shadow(c, 0, 0, 14, 2.6, 0.3, 8);
    const col = ['#ffd21e', '#ffc81a', '#ffdb3a'][d.variant ?? 0];
    block(c, -14, -10, 28, 8, col, shade(col, 0.2), shade(col, -0.25));
    block(c, -8, -16, 16, 7, col, shade(col, 0.2));
    rect(c, '#3a4a5a', -6.6, -14.6, 6, 5);
    rect(c, '#3a4a5a', 0.6, -14.6, 6, 5);
    rect(c, 'rgba(255,255,255,0.35)', -6, -14, 1.4, 4);
    // Roof light.
    block(c, -4, -19, 8, 3, night ? '#fff3c0' : '#ffffff');
    txt(c, 'TAXI', 0, -17.4, 2.2, '#c8202a', 900);
    rect(c, '#1b1d24', -14, -6.4, 28, 0.6);
    for (const x of [-9, 9]) {
      ellipse(c, '#1b1d24', x, -2, 2.8, 2.8);
      ellipse(c, '#9aa0a6', x, -2, 1.2, 1.2);
    }
    rect(c, night ? '#fff3c0' : '#e8e4da', 12.6, -9, 1.4, 1.6);
    rect(c, '#c8202a', -14, -9, 1, 1.6);
  });
}

function taxiSign(c: Ctx, d: Decor): void {
  art(c, d, 'taxiSign', 18, 40, (c) => {
    shadow(c, 0, 0, 3, 1);
    rect(c, '#55606a', -0.6, -30, 1.2, 30);
    block(c, -7, -36, 14, 10, '#ffd21e', '#ffe85a');
    txt(c, '計程車', 0, -32.6, 3.4, '#1b1d24', 900);
    txt(c, 'TAXI', 0, -28.6, 2.8, '#1b1d24', 900);
  });
}

/** Tour buses: the green-and-white Tourist Shuttle and a flashy Taiwanese tour coach. */
function bus(c: Ctx, d: Decor): void {
  const v = d.variant ?? 0;
  art(c, d, 'bus', 112, 40, (c, night) => {
    shadow(c, 0, 0, 54, 3, 0.32, 14);
    const L = -52, R = 52, top = -34, by = -4;
    const body = v ? '#f6f0f8' : '#ffffff';
    c.fillStyle = body;
    c.beginPath();
    c.moveTo(L + 4, top);
    c.lineTo(R, top);
    c.lineTo(R, by);
    c.lineTo(L, by);
    c.lineTo(L, top + 6);
    c.quadraticCurveTo(L, top, L + 4, top);
    c.fill();
    c.strokeStyle = '#8a9096';
    c.lineWidth = 0.7;
    c.stroke();
    if (v === 0) {
      // Green swoosh and the shuttle name.
      poly(c, '#2fa36a', [L, by - 8, R, by - 18, R, by - 10, L, by - 2]);
      poly(c, '#9ad86a', [L, by - 11, R, by - 20, R, by - 18, L, by - 8]);
      txt(c, '台灣好行', 14, by - 7, 5.4, '#ffffff', 900, 'rgba(0,0,0,0.3)');
      txt(c, 'Tourist Shuttle', -18, by - 3.8, 3, '#1f6a4a', 800);
    } else {
      // Purple-pink waves of a tour coach, with stars.
      poly(c, '#b03a9a', [L, by - 4, -10, by - 16, R, by - 8, R, by, L, by]);
      poly(c, '#ff7ab8', [L, by - 8, -10, by - 18, R, by - 11, R, by - 8, -10, by - 14, L, by - 4]);
      for (let i = 0; i < 4; i++) txt(c, '★', -34 + i * 22, by - 12 + (i % 2) * 3, 3.4, '#ffd34e', 900);
      txt(c, '遊覽車', 26, by - 3.6, 3.4, '#ffffff', 900);
    }
    // Windows.
    for (let i = 0; i < 6; i++) {
      const x = L + 14 + i * 14;
      rect(c, night ? '#ffe2a0' : '#2a3a4a', x, top + 4, 12, 10);
      if (!night) rect(c, 'rgba(255,255,255,0.3)', x + 1, top + 5, 3, 8);
    }
    // Windscreen and LED destination board.
    poly(c, '#2a3a4a', [L + 1, top + 16, L + 1, top + 6, L + 4, top + 1, L + 12, top + 1, L + 12, top + 16]);
    block(c, L + 2, top + 1.4, 10, 3.4, '#1b1d24');
    txt(c, v ? '花蓮' : '太魯閣', L + 7, top + 3.1, 2.4, '#ffb43a', 900);
    // Door.
    rect(c, '#3a4a5a', L + 14, by - 18, 8, 14);
    rect(c, 'rgba(255,255,255,0.25)', L + 15, by - 17, 2, 12);
    for (const x of [L + 22, R - 16]) {
      ellipse(c, '#1b1d24', x, by + 0.6, 4.2, 4.2);
      ellipse(c, '#9aa0a6', x, by + 0.6, 1.8, 1.8);
    }
    rect(c, night ? '#fff3c0' : '#e8e4da', L, by - 6, 1.8, 2);
  });
}

/** The Taiwan Tourist Shuttle stop (object): shelter, route sign, timetable. */
function shuttleStop(c: Ctx, r: Rect): void {
  artObj(c, r, 'shuttle', 44, 56, (c) => {
    shadow(c, 0, 0, 18, 2.6, 0.3, 30);
    // Shelter roof and back glass.
    for (const x of [-16, 13]) rect(c, '#55606a', x, -36, 2, 36);
    rect(c, 'rgba(170,210,230,0.55)', -15, -30, 29, 22);
    rect(c, '#55606a', -15, -8.6, 29, 0.8);
    block(c, -19, -40, 38, 5, '#2fa36a', '#4fc38a', '#1f7a4e');
    // Bench.
    block(c, -12, -9, 22, 3, '#a8784a', '#c8986a');
    // Route sign on its own pole.
    rect(c, '#55606a', 16.4, -46, 1.6, 46);
    block(c, 10, -54, 14, 12, '#ffffff', '#ffffff', '#c8ccd0');
    rect(c, '#2fa36a', 10.6, -53.4, 12.8, 3.4);
    txt(c, '台灣好行', 17, -51.6, 2.6, '#ffffff', 900);
    txt(c, '太魯閣線', 17, -47.6, 2.6, '#1f6a4a', 900);
    txt(c, 'Taroko Route', 17, -44.6, 1.7, '#55606a', 700);
    // Timetable on the shelter.
    block(c, -12, -27, 10, 12, '#f4f1ea', '#ffffff');
    for (let k = 0; k < 5; k++) rect(c, '#55606a', -10.6, -25 + k * 2, 7, 0.6);
    // Map of the route on the back glass.
    block(c, 1, -27, 11, 12, '#e8f4ec', '#ffffff');
    line(c, '#2fa36a', 0.8, [2.6, -17, 5, -22, 8, -20, 10.6, -25]);
    ellipse(c, '#c8202a', 2.6, -17, 0.9, 0.9);
    ellipse(c, '#3a8fd9', 10.6, -25, 0.9, 0.9);
  });
}

// ---------------------------------------------------------------------------
// Cultural and Creative Park
// ---------------------------------------------------------------------------

function warehouse(c: Ctx, d: Decor, kind: 0 | 1 | 2): void {
  const w = kind === 2 ? 112 : 128;
  art(c, d, `wh${kind}`, w, 88, (c, night) => {
    const L = -w / 2 + 2, R = w / 2 - 2;
    shadow(c, 0, 0, w / 2, 3.5, 0.3);
    if (kind === 0) {
      // Red-brick winery warehouse: the long roof recedes north behind its gable end.
      block(c, L - 2, -88, R - L + 4, 42, '#6a7278', '#7a8288', '#4a5258');
      for (let k = 0; k < 9; k++) rect(c, 'rgba(255,255,255,0.08)', L, -86 + k * 4.4, R - L, 0.6);
      poly(c, '#5a6066', [L - 2, -48, 0, -86, R + 2, -48]);
      poly(c, '#7a8288', [L + 4, -49, 0, -82, R - 4, -49]);
      for (let k = 1; k < 8; k++) line(c, 'rgba(40,40,40,0.25)', 0.5, [L + 4 + k * 3.8, -49 - k * 4.1, R - 4 - k * 3.8, -49 - k * 4.1]);
      block(c, L, -50, R - L, 50, '#a8503a', '#c06a50', '#7a3424');
      for (let y = -48; y < -2; y += 3) for (let x = L + ((y / 3) % 2 ? 2 : 0); x < R; x += 6) rect(c, 'rgba(60,20,10,0.25)', x, y, 0.5, 3);
      for (let y = -48; y < -2; y += 3) rect(c, 'rgba(255,230,210,0.12)', L + 1, y, R - L - 2, 0.4);
      // Round vent under the gable, big wooden door, two windows.
      ellipse(c, '#3a2a20', 0, -60, 5, 5);
      for (let k = 0; k < 4; k++) line(c, '#a8503a', 0.6, [-5 + k * 3.3, -64, -5 + k * 3.3, -56]);
      block(c, -14, -34, 28, 34, '#6a4428', '#8a5a34', '#4a2e18');
      line(c, '#4a2e18', 0.6, [-14, -34, 14, 0]);
      line(c, '#4a2e18', 0.6, [14, -34, -14, 0]);
      for (const x of [L + 10, R - 26]) pane(c, x, -38, 16, 12, night, x, '#e8e4da');
      sign(c, '酒廠 A1', 0, -47, 34, '#2a1810', '#ffd34e', 5);
    } else if (kind === 1) {
      // White stucco warehouse with a dark tiled hip roof and banners.
      block(c, L - 2, -86, R - L + 4, 30, '#3a3e44', '#5a5e64', '#2a2e34');
      for (let y = -84; y < -58; y += 3) rect(c, 'rgba(255,255,255,0.08)', L, y, R - L, 0.6);
      block(c, L, -58, R - L, 58, '#f2eee4', '#ffffff', '#d4ccbc');
      rect(c, '#3a3e44', L, -14, R - L, 14);
      for (let i = 0; i < 4; i++) {
        const x = L + 8 + i * 31;
        block(c, x, -40, 18, 40, '#5a3a24', '#7a5030');
        if (night) rect(c, 'rgba(255,220,150,0.6)', x + 2, -38, 14, 24);
        pane(c, x + 1, -52, 16, 8, night, i + 30);
      }
      // Art banners.
      const cols = ['#e8473a', '#3aa0d9', '#ffd34e'];
      for (let i = 0; i < 3; i++) {
        const x = L + 24 + i * 31;
        rect(c, '#55606a', x, -56, 0.6, 30);
        block(c, x + 0.6, -56, 7, 22, cols[i]);
        vtxt(c, ['藝術', '展覽', '音樂'][i], x + 4.1, -51, 4, '#ffffff');
      }
    } else {
      // A small café warehouse with string lights.
      block(c, L - 2, -66, R - L + 4, 28, '#5a6a5a', '#6a7a6a', '#3a4a3a');
      for (let k = 0; k < 6; k++) rect(c, 'rgba(255,255,255,0.08)', L, -64 + k * 4.4, R - L, 0.6);
      poly(c, '#4a5a4a', [L - 2, -40, 0, -64, R + 2, -40]);
      poly(c, '#6a7a6a', [L + 4, -41, 0, -60, R - 4, -41]);
      block(c, L, -42, R - L, 42, '#e8e0d0', '#f6f0e4', '#c8bca8');
      block(c, -12, -28, 24, 28, '#2a3a4a');
      if (night) rect(c, 'rgba(255,220,150,0.75)', -10, -26, 20, 24);
      else rect(c, 'rgba(160,200,220,0.6)', -10, -26, 20, 24);
      for (const x of [L + 6, R - 22]) pane(c, x, -32, 16, 14, night, x + 3);
      sign(c, '文創咖啡', 0, -40, 30, '#3a5a3a', '#fff3d0', 5);
      for (let i = 0; i < 9; i++) {
        const x = L + 4 + i * 12.5;
        ellipse(c, night ? '#fff3b0' : '#e8e0c0', x, -43 + Math.sin(i) * 1.2, 1, 1);
      }
    }
  });
}

function artCube(c: Ctx, d: Decor): void {
  art(c, d, 'artCube', 40, 36, (c) => {
    shadow(c, 0, 0, 16, 3, 0.3);
    const cols = ['#e8473a', '#ffb43a', '#ffd34e', '#57c278', '#3aa0d9', '#7d5aa8'];
    'HUALIEN'.split('').forEach((ch, i) => {
      const x = -17 + i * 5, y = -10 - (i % 2) * 3;
      block(c, x, y - 8, 5, 8, cols[i % cols.length]);
      txt(c, ch, x + 2.5, y - 4, 4.6, '#ffffff', 900);
    });
    rect(c, '#8a8580', -18, -2, 36, 2);
  }, false);
}

// ---------------------------------------------------------------------------
// Pine Garden (松園別館) and its Ryukyu pines
// ---------------------------------------------------------------------------

function pineGarden(c: Ctx, night: boolean): void {
  const W = 192, L = -W / 2, R = W / 2;
  shadow(c, 0, 0, W / 2, 4, 0.3);
  // Hipped roof of grey Japanese tiles with a dark ridge.
  poly(c, '#3e464e', [L - 4, -86, L + 30, -118, R - 30, -118, R + 4, -86]);
  for (let k = 0; k < 9; k++) {
    const y = -88 - k * 3.4, inset = 4 + k * 3.6;
    line(c, 'rgba(255,255,255,0.12)', 0.6, [L + inset, y, R - inset, y]);
  }
  rect(c, '#2a3036', L + 30, -120, R - L - 60, 3);
  for (const x of [L + 28, R - 32]) block(c, x, -122, 4, 5, '#2a3036');
  rect(c, '#2a3036', L - 4, -87, W + 8, 2);
  // Upper floor: white wall with an arcaded verandah.
  block(c, L + 4, -85, W - 8, 38, '#f6f2ea', '#ffffff', '#d8d0c0');
  const arches = 9;
  for (let i = 0; i < arches; i++) {
    const x = L + 12 + i * ((W - 24) / arches);
    const aw = (W - 24) / arches - 4;
    const cx = x + aw / 2;
    c.fillStyle = night ? '#ffd88a' : '#3a4048';
    c.beginPath();
    c.moveTo(x, -50);
    c.lineTo(x, -72);
    c.arc(cx, -72, aw / 2, Math.PI, 0);
    c.lineTo(x + aw, -50);
    c.closePath();
    c.fill();
    if (!night) rect(c, 'rgba(255,255,255,0.12)', x + 1, -72, 2, 20);
    // Balustrade.
    rect(c, '#ffffff', x - 1, -58, aw + 2, 1.4);
    for (let k = 1; k < 4; k++) rect(c, '#ffffff', x + (k * aw) / 4, -58, 0.9, 8);
  }
  rect(c, '#e4dccc', L + 4, -50, W - 8, 3);
  // Ground floor arcade (deeper arches, darker shade).
  block(c, L + 4, -47, W - 8, 47, '#f2ece2', '#ffffff', '#d0c8b8');
  for (let i = 0; i < arches; i++) {
    const x = L + 12 + i * ((W - 24) / arches);
    const aw = (W - 24) / arches - 4;
    const cx = x + aw / 2;
    c.fillStyle = night ? '#f2c070' : '#2a3036';
    c.beginPath();
    c.moveTo(x, -1);
    c.lineTo(x, -30);
    c.arc(cx, -30, aw / 2, Math.PI, 0);
    c.lineTo(x + aw, -1);
    c.closePath();
    c.fill();
    // Doors and windows inside the arcade.
    if (i % 2 === 0) block(c, cx - 4, -22, 8, 21, '#6a4a2a', '#8a6440');
    else pane(c, cx - 5, -26, 10, 10, night, i + 50, '#d8d0c0');
  }
  // Pillars between arches and the stone plinth.
  for (let i = 0; i <= arches; i++) {
    const x = L + 10 + i * ((W - 24) / arches);
    rect(c, 'rgba(0,0,0,0.06)', x, -46, 1, 46);
  }
  rect(c, '#9a948a', L + 2, -3, W - 4, 3);
  // Name board over the central arch and the steps.
  block(c, -20, -48, 40, 7, '#3a2a20', '#5a4030');
  txt(c, '松園別館', 0, -44.4, 5.2, '#f4e6c0', 900);
  for (let k = 0; k < 2; k++) rect(c, '#b8b2a6', -12 + k * 2, -1 + k, 24 - k * 4, 1.2);
}

/** Ryukyu pine: a crooked trunk and wide, flat, wind-swept pads of needles. */
function ryukyuPine(c: Ctx, d: Decor): void {
  const v = (d.variant ?? 0) % 3;
  art(c, d, 'ryuPine', 72, 78, (c) => {
    shadow(c, 0, 0, 20, 4, 0.3, 50);
    const lean = [6, -8, 3][v];
    c.strokeStyle = '#4a3020';
    c.lineCap = 'round';
    c.lineWidth = 4.4;
    c.beginPath();
    c.moveTo(0, 0);
    c.bezierCurveTo(-2, -16, lean + 4, -26, lean, -42);
    c.stroke();
    c.lineWidth = 2.2;
    line(c, '#4a3020', 2.2, [lean * 0.4, -26, -14 + v * 4, -40]);
    line(c, '#4a3020', 2, [lean, -40, lean + 16 - v * 6, -52]);
    rect(c, 'rgba(255,220,180,0.25)', -1, -14, 1, 10);
    const pads: [number, number, number][] = [
      [lean - 16 + v * 4, -42, 15],
      [lean + 14 - v * 5, -54, 14],
      [lean - 2, -62, 12],
      [lean + 20, -40, 9],
      [lean - 24 + v * 6, -54, 9],
    ];
    for (const [x, y, rw] of pads) {
      ellipse(c, '#1f3a26', x, y + 1.6, rw + 1, rw * 0.36 + 1);
      ellipse(c, '#2f5a36', x, y, rw, rw * 0.36);
      ellipse(c, '#4f7a46', x - rw * 0.2, y - rw * 0.12, rw * 0.7, rw * 0.2);
      for (let k = 0; k < 5; k++) ellipse(c, '#6f9a5a', x - rw * 0.6 + k * rw * 0.3, y - rw * 0.2, 1.2, 0.6);
    }
  }, false);
}

function pineStone(c: Ctx, r: Rect): void {
  artObj(c, r, 'pineStone', 48, 40, (c) => {
    shadow(c, 0, 0, 18, 3, 0.3);
    poly(c, '#7a7870', [-18, 0, -20, -16, -12, -26, 8, -28, 19, -18, 18, 0]);
    poly(c, '#9a968c', [-16, -2, -17, -15, -11, -24, 7, -26, 16, -17, 15, -2]);
    rect(c, 'rgba(255,255,255,0.2)', -10, -23, 14, 0.8);
    txt(c, '松園別館', 0, -16, 6.2, '#2a2420', 900);
    txt(c, 'PINE GARDEN · 1942', 0, -8.6, 2.4, '#3a3630', 800);
    for (const x of [-14, 14]) ellipse(c, '#5f8a4a', x, -1, 4, 2);
  });
}

// ---------------------------------------------------------------------------
// Downtown: shophouses with arcades (騎樓), the Earth God shrine
// ---------------------------------------------------------------------------

const SHOP_NAMES = ['小吃', '茶', '藥局', '書店', '麵包', '眼鏡', '冰店', '文具', '花蓮薯', '剉冰', '早餐', '手機', '牛肉麵', '銀行', '診所', '咖啡', '鞋店', '米店', '五金', '伴手禮'];
const FACADES = ['#f2e6d0', '#e8d8c8', '#f0d8d0', '#dfe4e0', '#e6e0f0', '#f4ecd4', '#d8e4ea'];
const SIGN_BG = ['#c0392b', '#1f6f9a', '#2fa36a', '#e2a03a', '#7d5aa8', '#2a1810', '#d0602a'];

function shops(c: Ctx, d: Decor): void {
  const w = d.w ?? 160;
  const units = Math.round(w / 32);
  art(c, d, `shops${d.x},${d.y}`, w, 72, (c, night) => {
    const L = -w / 2;
    shadow(c, 0, 0, w / 2, 3, 0.3);
    for (let u = 0; u < units; u++) {
      const x = L + u * 32;
      const seed = Math.round(d.x / 16) * 31 + Math.round(d.y / 16) * 7 + u;
      const floors = rnd(seed, 1) < 0.35 ? 3 : 2;
      const facade = FACADES[Math.floor(rnd(seed, 2) * FACADES.length)];
      const top = floors === 3 ? -64 : -52;
      // Rooftop: a sheet-metal add-on or a water tank.
      if (rnd(seed, 3) < 0.5) {
        block(c, x + 3, top - 7, 18, 7, '#7f9aa8', '#a8c0cc', '#5a7480');
        for (let k = 0; k < 6; k++) rect(c, 'rgba(0,0,0,0.15)', x + 4 + k * 3, top - 6, 0.5, 6);
      } else {
        block(c, x + 20, top - 9, 8, 7, '#e8e4da', '#ffffff');
        rect(c, '#55606a', x + 21, top - 2, 1, 2);
        rect(c, '#55606a', x + 26, top - 2, 1, 2);
      }
      // Facade with little square tiles.
      block(c, x + 0.5, top, 31, -top, facade, shade(facade, 0.15), shade(facade, -0.2));
      for (let y = top + 2; y < -24; y += 2.4) rect(c, 'rgba(0,0,0,0.05)', x + 1, y, 30, 0.4);
      // Upper windows with iron grilles and an AC unit.
      for (let f = 0; f < floors - 1; f++) {
        const wy = -40 - f * 12;
        pane(c, x + 5, wy, 22, 8, night, seed * 3 + f);
        for (let k = 0; k < 6; k++) rect(c, 'rgba(60,60,60,0.55)', x + 5 + k * 4.2, wy - 1, 0.5, 10);
        rect(c, 'rgba(60,60,60,0.55)', x + 4, wy + 8.6, 24, 0.6);
        if (rnd(seed, 4 + f) < 0.5) block(c, x + 22, wy + 4, 7, 5, '#e8e4da', '#ffffff');
      }
      // Horizontal shop sign over the arcade.
      const name = SHOP_NAMES[Math.floor(rnd(seed, 5) * SHOP_NAMES.length)];
      const bg = SIGN_BG[Math.floor(rnd(seed, 6) * SIGN_BG.length)];
      block(c, x + 1.5, -28, 29, 7.6, bg, shade(bg, 0.25), shade(bg, -0.3));
      txt(c, name, x + 16, -24.1, name.length > 2 ? 4.6 : 5.6, '#fff6dc', 900, 'rgba(0,0,0,0.35)');
      if (night) {
        c.globalCompositeOperation = 'lighter';
        ellipse(c, 'rgba(255,200,140,0.25)', x + 16, -24, 18, 6);
        c.globalCompositeOperation = 'source-over';
      }
      // Arcade: dark recess, shop interior, pillars.
      rect(c, '#3a3430', x + 1, -20, 30, 20);
      rect(c, night ? 'rgba(255,214,150,0.55)' : 'rgba(200,190,170,0.35)', x + 4, -17, 24, 14);
      const goods = ['#e8473a', '#ffd34e', '#3aa0d9', '#57c278', '#f4f1ea'];
      for (let k = 0; k < 5; k++) block(c, x + 5 + k * 4.6, -13 + (k % 2) * 2, 3.4, 4, goods[(k + seed) % goods.length]);
      rect(c, 'rgba(0,0,0,0.25)', x + 1, -20, 30, 2);
      block(c, x, -20, 3, 20, shade(facade, -0.08), shade(facade, 0.1));
      // Vertical sign jutting out on some units.
      if (rnd(seed, 8) < 0.45) {
        const vs = SHOP_NAMES[Math.floor(rnd(seed, 9) * SHOP_NAMES.length)];
        const vb = SIGN_BG[Math.floor(rnd(seed, 10) * SIGN_BG.length)];
        const sh = Math.max(14, vs.length * 6 + 4);
        block(c, x + 26, top + 2, 6, sh, vb, shade(vb, 0.2), shade(vb, -0.3));
        vtxt(c, vs, x + 29, top + 6, 4.4, '#fff3b0', 1.25);
      }
      rect(c, '#b8b2a6', x, -1.4, 32, 1.4);
    }
    block(c, w / 2 - 3, -20, 3, 20, '#d8d0c0', '#ffffff');
  });
}

function shrine(c: Ctx, r: Rect, t: number): void {
  artObj(c, r, 'shrine', 30, 40, (c, night) => {
    shadow(c, 0, 0, 12, 2.4, 0.3);
    block(c, -10, -22, 20, 22, '#c0392b', '#e05a48', '#8a2a1e');
    rect(c, '#2a1810', -6, -18, 12, 12);
    ellipse(c, night ? '#ffb070' : '#d8a060', 0, -11, 2.6, 3);
    ellipse(c, '#f4e6c0', 0, -15, 1.6, 1.6);
    // Curved roof with upturned ends.
    poly(c, '#2f8a6a', [-14, -22, 14, -22, 10, -28, -10, -28]);
    poly(c, '#2f8a6a', [-14, -22, -16, -27, -11, -24]);
    poly(c, '#2f8a6a', [14, -22, 16, -27, 11, -24]);
    rect(c, '#ffd34e', -10, -28.6, 20, 0.8);
    ellipse(c, '#ffd34e', 0, -30, 1.6, 1.6);
    txt(c, '福德祠', 0, -20, 3, '#ffd34e', 900);
    // Incense burner and fruit.
    block(c, -4, -5, 8, 5, '#b8901a', '#d8b03a');
    for (const x of [-7, 7]) ellipse(c, x < 0 ? '#ff9f43' : '#e8473a', x, -6, 1.6, 1.6);
  });
  // Incense smoke.
  const cx = r.x + r.w / 2, by = r.y + r.h;
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.4 + i / 2) % 1;
    ellipse(c, `rgba(230,230,230,${0.4 * (1 - k)})`, cx + Math.sin(t + i * 3) * 1.6 + k * 2, by - 6 - k * 14, 0.8 + k * 1.6, 0.8 + k);
  }
}

// ---------------------------------------------------------------------------
// Night-market stalls (closed under a tarp by day)
// ---------------------------------------------------------------------------

function stallFrame(c: Ctx, r: Rect, t: number, label: string, canopy: string, signBg: string, goods: (c: Ctx, r: Rect, t: number) => void): void {
  const { x, y, w, h } = r;
  shadow(c, x + w / 2, y + h, w / 2 + 2, 3.5, 0.35);
  if (!marketOpen()) {
    // Covered with a blue-and-white striped tarp, tied with rope.
    rect(c, OUTLINE, x + 1, y - 24, 2, 26);
    rect(c, OUTLINE, x + w - 3, y - 24, 2, 26);
    c.fillStyle = '#3a6fb0';
    c.beginPath();
    c.moveTo(x - 1, y + h);
    c.lineTo(x - 2, y - 14);
    c.quadraticCurveTo(x + w / 2, y - 30, x + w + 2, y - 14);
    c.lineTo(x + w + 1, y + h);
    c.closePath();
    c.fill();
    for (let i = 4; i < w; i += 8) rect(c, 'rgba(255,255,255,0.35)', x + i, y - 18 + Math.abs(i - w / 2) * 0.12, 3, h + 18);
    rect(c, '#c8a060', x - 1, y - 6, w + 2, 0.8);
    sign(c, label, x + w / 2, y - 34, Math.max(28, label.length * 9 + 8), shade(signBg, -0.35), '#c8c0b0');
    return;
  }
  rect(c, OUTLINE, x + 1, y - 34, 2, 36);
  rect(c, OUTLINE, x + w - 3, y - 34, 2, 36);
  const top = y - 40;
  for (let i = 0; i < w; i += 6) {
    c.fillStyle = (i / 6) % 2 ? '#fff6e6' : canopy;
    c.fillRect(x - 2 + i, top, Math.min(6, w + 4 - i), 9);
    c.beginPath();
    c.arc(x + 1 + i, top + 9, 3, 0, Math.PI);
    c.fill();
  }
  rect(c, 'rgba(0,0,0,0.25)', x - 2, top + 9, w + 4, 1);
  const sw = Math.max(28, label.length * 9 + 8);
  sign(c, label, x + w / 2, top - 11, sw, signBg);
  if (isNight()) {
    c.globalCompositeOperation = 'lighter';
    ellipse(c, 'rgba(255,170,90,0.22)', x + w / 2, top - 6, sw / 2 + 4, 7);
    c.globalCompositeOperation = 'source-over';
  }
  const sway = wind.at(x, y) * 1.2 + Math.sin(t * 1.7 + x) * 0.6;
  for (const bx of [x + w * 0.25, x + w * 0.75]) {
    line(c, OUTLINE, 0.6, [bx, top + 9, bx + sway, top + 16]);
    ellipse(c, '#fff3b0', bx + sway, top + 17.5, 1.8, 2);
    ellipse(c, 'rgba(255,230,140,0.25)', bx + sway, top + 18, 6, 5);
  }
  goods(c, r, t);
  block(c, x, y - 8, w, h + 8, '#9a6a44', '#b88a5c', '#6e4a2e');
  rect(c, '#f2e6cf', x + 1, y - 9, w - 2, 2);
  for (let i = 4; i < w - 2; i += 8) rect(c, 'rgba(0,0,0,0.15)', x + i, y - 6, 0.6, h + 4);
}

const STALLS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  hc_dumpling: (c, r, t) =>
    stallFrame(c, r, t, '水餃', '#e2493b', '#c0392b', (c, r, t) => {
      const px = r.x + 12, py = r.y - 11;
      block(c, px - 9, py - 10, 18, 11, '#b8c0c8', '#dce2e8', '#8a9096');
      ellipse(c, '#e8e8e0', px, py - 10, 8.4, 2.2);
      steam(c, px, py - 12, t, 3, 0.45);
      for (let i = 0; i < 2; i++) {
        const tx = r.x + 26 + i * 10;
        block(c, tx, r.y - 13, 9, 3, '#c8a070', '#e0bc8c');
        for (let k = 0; k < 4; k++) ellipse(c, '#fbf6ea', tx + 1.6 + k * 2, r.y - 14, 1.2, 1);
      }
    }),
  hc_beefNoodle: (c, r, t) =>
    stallFrame(c, r, t, '牛肉麵', '#8a3a24', '#7a2a1a', (c, r, t) => {
      const px = r.x + 13, py = r.y - 11;
      block(c, px - 10, py - 12, 20, 13, '#4a4e54', '#6a6e74', '#2a2e34');
      ellipse(c, '#8a3a1a', px, py - 12, 9.2, 2.4);
      ellipse(c, '#b85a2a', px - 2, py - 12.4, 4, 1);
      steam(c, px, py - 14, t, 3, 0.5);
      for (let i = 0; i < 2; i++) {
        const bx = r.x + 31 + i * 8;
        ellipse(c, '#f4f1ea', bx, r.y - 11, 3.4, 1.6);
        ellipse(c, '#a8501a', bx, r.y - 11.6, 2.6, 0.9);
        rect(c, '#3f9a3a', bx - 1, r.y - 12, 0.8, 0.5);
      }
    }),
  hc_xlb: (c, r, t) =>
    stallFrame(c, r, t, '小籠包', '#d9b450', '#a8782a', (c, r, t) => {
      for (let i = 0; i < 3; i++) {
        const bx = r.x + 10 + i * 13;
        for (let k = 0; k < 3 - (i % 2); k++) block(c, bx - 5, r.y - 13 - k * 4, 10, 4, '#c8a060', '#e0c08a', '#8a6a3a');
        steam(c, bx, r.y - 14 - (3 - (i % 2)) * 4, t + i * 0.3, 2, 0.45);
      }
    }),
  hc_sausage: (c, r, t) =>
    stallFrame(c, r, t, '烤香腸', '#c8202a', '#1b1d24', (c, r, t) => {
      // Charcoal grill with glowing coals and a row of sausages; smoke.
      block(c, r.x + 4, r.y - 14, r.w - 8, 5, '#2a2e34', '#4a4e54');
      for (let i = 0; i < 9; i++) ellipse(c, `rgba(255,${110 + ((i * 37) % 60)},40,${0.6 + 0.3 * Math.sin(t * 5 + i)})`, r.x + 7 + i * 4.2, r.y - 11.4, 1.6, 0.7);
      for (let i = 0; i < 6; i++) {
        const sx = r.x + 8 + i * 6;
        block(c, sx, r.y - 17, 4.4, 2.6, '#a83a2a', '#d05a3a');
        rect(c, '#5a1a10', sx + 1.4, r.y - 16.6, 0.5, 2);
      }
      for (let i = 0; i < 3; i++) {
        const k = (t * 0.35 + i / 3) % 1;
        ellipse(c, `rgba(200,200,200,${0.35 * (1 - k)})`, r.x + 14 + i * 9 + Math.sin(t + i) * 2, r.y - 19 - k * 16, 2 + k * 3, 1.6 + k * 2);
      }
      amisBand(c, r.x, r.y - 8.6, r.w, 2.4, 0);
    }),
  hc_bamboo: (c, r, t) =>
    stallFrame(c, r, t, '竹筒飯', '#5f8a3a', '#3a5a2a', (c, r, t) => {
      for (let i = 0; i < 7; i++) {
        const bx = r.x + 5 + i * 5.4;
        block(c, bx, r.y - 22 + (i % 2) * 2, 4, 14 - (i % 2) * 2, '#6f9a3a', '#9ac85a', '#4a6a2a');
        rect(c, '#3a5a1a', bx, r.y - 16, 4, 0.6);
      }
      // One split open, showing the rice.
      block(c, r.x + r.w - 12, r.y - 14, 9, 4, '#8aba4a', '#aad86a');
      ellipse(c, '#fbf6ea', r.x + r.w - 7.6, r.y - 13.4, 3.6, 1.2);
      steam(c, r.x + r.w - 8, r.y - 15, t, 2, 0.4);
      amisBand(c, r.x, r.y - 8.6, r.w, 2.4, 1);
    }),
  hc_crafts: (c, r, t) =>
    stallFrame(c, r, t, '手工藝', '#e0302a', '#1b1d24', (c, r) => {
      // Woven cloth with Amis patterns hanging behind; bead bracelets on the counter.
      amisBand(c, r.x + 3, r.y - 30, r.w - 6, 5, 0);
      amisBand(c, r.x + 3, r.y - 25, r.w - 6, 4, 1);
      amisBand(c, r.x + 3, r.y - 21, r.w - 6, 4, 2);
      const beads = ['#ffd34e', '#e8473a', '#3aa0d9', '#5fbf5a', '#ffffff', '#ff8ac0'];
      for (let i = 0; i < 6; i++) {
        const bx = r.x + 6 + i * 6.4;
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          ellipse(c, beads[(i + k) % beads.length], bx + Math.cos(a) * 2, r.y - 12 + Math.sin(a) * 0.9, 0.7, 0.7);
        }
      }
      block(c, r.x + r.w - 9, r.y - 18, 7, 7, '#c89a5a', '#e0b87a');
      for (let k = 0; k < 3; k++) rect(c, '#8a5a2a', r.x + r.w - 8, r.y - 16 + k * 2, 5, 0.5);
    }),
  hc_icecream: (c, r, t) =>
    stallFrame(c, r, t, '花生捲冰淇淋', '#ffb0c8', '#d0602a', (c, r) => {
      // A block of peanut brittle and a plane, pancake wrappers, ice-cream tubs.
      block(c, r.x + 4, r.y - 16, 14, 7, '#d9a050', '#f0c070', '#a8782a');
      for (let k = 0; k < 4; k++) rect(c, 'rgba(120,70,20,0.4)', r.x + 6 + k * 3, r.y - 15, 1, 5);
      line(c, '#9aa0a6', 1, [r.x + 8, r.y - 18, r.x + 16, r.y - 21]);
      for (let i = 0; i < 3; i++) {
        const bx = r.x + 24 + i * 6;
        block(c, bx, r.y - 14, 5, 5, '#f4f1ea', '#ffffff');
        ellipse(c, ['#ff9fc0', '#fff3d0', '#b88a5c'][i], bx + 2.5, r.y - 14, 2.2, 1);
      }
      ellipse(c, '#fbf6ea', r.x + r.w - 6, r.y - 11, 4, 1.4);
    }),
  hc_tea: (c, r) =>
    stallFrame(c, r, 0, '珍珠奶茶', '#3aa0d9', '#1f6f9a', (c, r) => {
      for (let i = 0; i < 5; i++) {
        const bx = r.x + 6 + i * 7;
        block(c, bx, r.y - 18, 5, 9, 'rgba(240,240,240,0.9)', '#ffffff');
        rect(c, i % 2 ? '#d8b088' : '#c8a070', bx + 0.6, r.y - 16, 3.8, 6);
        for (let k = 0; k < 3; k++) ellipse(c, '#2a1810', bx + 1.4 + k * 1.2, r.y - 10.8, 0.6, 0.6);
        rect(c, ['#e8473a', '#57c278', '#ffd34e', '#3aa0d9', '#ff9fc0'][i], bx + 2.4, r.y - 21, 0.8, 4);
      }
    }),
  hc_balloon: (c, r, t) =>
    stallFrame(c, r, t, '射氣球', '#7d5aa8', '#5e4282', (c, r, t) => {
      // A board covered in balloons; a few pop and reappear.
      block(c, r.x + 3, r.y - 34, r.w - 6, 24, '#f4f1ea', '#ffffff', '#c8c0b0');
      const cols = ['#e8473a', '#ffd34e', '#3aa0d9', '#57c278', '#ff8ac0', '#ff9f43'];
      for (let j = 0; j < 4; j++) for (let i = 0; i < 7; i++) {
        const k = j * 7 + i;
        if ((Math.floor(t * 0.7) + k * 5) % 13 === 0) {
          txt(c, '✶', r.x + 7 + i * 5.4, r.y - 30 + j * 5.6, 3.6, '#ffd34e');
          continue;
        }
        ellipse(c, cols[k % cols.length], r.x + 7 + i * 5.4, r.y - 30 + j * 5.6, 2.2, 2.5);
        ellipse(c, 'rgba(255,255,255,0.6)', r.x + 6.4 + i * 5.4, r.y - 31 + j * 5.6, 0.6, 0.8);
      }
      // Darts on the counter, prizes hanging.
      for (let i = 0; i < 4; i++) line(c, '#3a3e44', 0.6, [r.x + 8 + i * 4, r.y - 10, r.x + 11 + i * 4, r.y - 12]);
    }),
};

/** Extra (non-interactive) stalls that fill the market: squid, corn, candied fruit, shaved ice. */
const DECO_STALLS: [string, string, string, (c: Ctx, r: Rect, t: number) => void][] = [
  ['烤魷魚', '#3a8fd9', '#1f4a7a', (c, r, t) => {
    for (let i = 0; i < 4; i++) {
      const sx = r.x + 8 + i * 9;
      rect(c, '#8a6a44', sx + 1.6, r.y - 26, 0.6, 12);
      ellipse(c, '#e8a060', sx + 2, r.y - 22, 3, 5);
      for (let k = 0; k < 3; k++) rect(c, '#d08040', sx + k * 1.6, r.y - 17, 0.6, 3);
    }
    steam(c, r.x + r.w / 2, r.y - 14, t, 2, 0.3);
  }],
  ['烤玉米', '#e2a03a', '#a8782a', (c, r, t) => {
    block(c, r.x + 4, r.y - 13, r.w - 8, 4, '#2a2e34');
    for (let i = 0; i < 6; i++) ellipse(c, i % 2 ? '#e8b030' : '#d89a20', r.x + 9 + i * 6, r.y - 15, 2.2, 4);
    steam(c, r.x + 18, r.y - 18, t, 2, 0.35);
  }],
  ['糖葫蘆', '#e8473a', '#c0392b', (c, r) => {
    for (let i = 0; i < 7; i++) {
      const sx = r.x + 6 + i * 6;
      rect(c, '#c8a060', sx, r.y - 30, 0.6, 18);
      for (let k = 0; k < 4; k++) ellipse(c, k % 2 ? '#e8302a' : '#ff5a4a', sx + 0.3, r.y - 28 + k * 3.4, 1.8, 1.6);
    }
  }],
  ['剉冰', '#57c2c8', '#2f8a9a', (c, r) => {
    for (let i = 0; i < 4; i++) {
      const bx = r.x + 8 + i * 10;
      ellipse(c, '#f4f8fa', bx, r.y - 13, 4, 3);
      ellipse(c, ['#ff7a9a', '#ffd34e', '#9ad86a', '#c8a070'][i], bx, r.y - 15, 3, 1.4);
    }
  }],
];

function decoStall(c: Ctx, d: Decor, t: number): void {
  const [label, canopy, bg, goods] = DECO_STALLS[(d.variant ?? 0) % DECO_STALLS.length];
  stallFrame(c, { x: d.x - 24, y: d.y - 12, w: 48, h: 12 }, t, label, canopy, bg, goods);
}

// ---------------------------------------------------------------------------
// Dongdamen: the gate, alley signs, the stage, carved post, crowds, the map board
// ---------------------------------------------------------------------------

function gate(c: Ctx, d: Decor, t: number): void {
  const night = isNight();
  art(c, d, 'gate', 150, 92, (c, night) => {
    for (const x of [-60, 60]) {
      shadow(c, x, 0, 7, 2.2, 0.32, 70);
      block(c, x - 5, -70, 10, 70, '#b02a22', '#d84a3a', '#7a1a14');
      for (let k = 0; k < 6; k++) rect(c, '#ffd34e', x - 5, -64 + k * 11, 10, 0.8);
      block(c, x - 6.5, -4, 13, 4, '#3a3e44');
    }
    // Arched beam.
    c.strokeStyle = '#b02a22';
    c.lineWidth = 7;
    c.beginPath();
    c.moveTo(-62, -64);
    c.quadraticCurveTo(0, -92, 62, -64);
    c.stroke();
    c.strokeStyle = '#ffd34e';
    c.lineWidth = 0.8;
    c.beginPath();
    c.moveTo(-62, -67.5);
    c.quadraticCurveTo(0, -95.5, 62, -67.5);
    c.stroke();
    // The big sign board.
    block(c, -44, -84, 88, 18, '#1b1d24', '#2a2e34', '#0a0c10');
    rect(c, '#ffd34e', -43, -83, 86, 0.8);
    rect(c, '#ffd34e', -43, -67.8, 86, 0.8);
    txt(c, '東大門夜市', 0, -77.4, 11, night ? '#fff07a' : '#ffd34e', 900, 'rgba(0,0,0,0.4)');
    txt(c, 'DONGDAMEN NIGHT MARKET', 0, -70, 3.4, '#ff9a7a', 800);
    // Hanging lanterns under the board.
    for (let i = 0; i < 5; i++) {
      const lx = -36 + i * 18;
      line(c, OUTLINE, 0.5, [lx, -66, lx, -62]);
      ellipse(c, i % 2 ? '#ffb43a' : '#e2493b', lx, -58, 3.4, 4);
      rect(c, '#2a1810', lx - 2, -62.4, 4, 0.8);
      rect(c, '#2a1810', lx - 2, -54.4, 4, 0.8);
    }
  });
  if (!night) return;
  // Chasing bulbs along the arch.
  const n = 22;
  for (let i = 0; i <= n; i++) {
    const k = i / n;
    const x = -62 + k * 124;
    const y = -64 + -28 * 4 * k * (1 - k) * 1 + -3.5;
    const on = (Math.floor(t * 6) + i) % 3 !== 0;
    ellipse(c, on ? '#fff3b0' : '#a8783a', d.x + x, d.y + y, 1, 1);
  }
  c.globalCompositeOperation = 'lighter';
  ellipse(c, 'rgba(255,200,90,0.28)', d.x, d.y - 77, 50, 12);
  for (let i = 0; i < 5; i++) ellipse(c, 'rgba(255,150,80,0.25)', d.x - 36 + i * 18, d.y - 58, 6, 6);
  c.globalCompositeOperation = 'source-over';
}

const ALLEYS: [string, string, string][] = [
  ['原住民一條街', '#c8202a', 'INDIGENOUS ST.'],
  ['外省一條街', '#1f4a7a', 'MAINLAND ALLEY'],
  ['福町夜市', '#d0602a', 'FUDING'],
  ['自強夜市', '#2f8a6a', 'ZIQIANG'],
];

function alleySign(c: Ctx, d: Decor): void {
  const [name, bg, en] = ALLEYS[(d.variant ?? 0) % 4];
  const h = name.length * 7 + 26;
  art(c, d, 'alley', 22, h + 30, (c, night) => {
    shadow(c, 0, 0, 4, 1.4, 0.3, 30);
    rect(c, '#3a3e44', -0.8, -h - 20, 1.6, h + 20);
    block(c, -6, -h - 18, 12, h, bg, shade(bg, 0.25), shade(bg, -0.3));
    if ((d.variant ?? 0) === 0) {
      amisBand(c, -6, -h - 18, 12, 3, 1);
      amisBand(c, -6, -21, 12, 3, 1);
    }
    vtxt(c, name, 0, -h - 11, 6, night ? '#fff07a' : '#fff3d0', 1.15, 'rgba(0,0,0,0.4)');
    txt(c, en, 0, -14, 1.9, '#3a3e44', 800);
  });
}

function stage(c: Ctx, d: Decor, t: number): void {
  const open = marketOpen();
  art(c, d, `stage${open ? 'O' : 'C'}`, 116, 92, (c, night) => {
    // Backdrop wall (behind the singer, anchored at the decor y), with truss and speakers.
    block(c, -52, -66, 104, 64, '#2a2030', '#3a2a40', '#1a1420');
    if (open) {
      vgrad(c, -46, -60, 92, 40, '#5a2a7a', '#c03a6a');
      txt(c, '東大門 LIVE', 0, -48, 9, '#fff07a', 900, 'rgba(0,0,0,0.4)');
      txt(c, '原住民音樂 · 民歌 · 流行', 0, -37, 4.2, '#ffd8f0', 800);
      for (let i = 0; i < 12; i++) ellipse(c, 'rgba(255,255,255,0.7)', -44 + i * 8, -23, 0.7, 0.7);
    } else {
      rect(c, '#4a3a50', -46, -60, 92, 40);
      txt(c, '東大門 LIVE', 0, -46, 8, '#8a7a90', 900);
      txt(c, '17:00 – 24:00', 0, -35, 4.4, '#a89ab0', 800);
    }
    amisBand(c, -52, -66, 104, 4, 0);
    // Truss.
    rect(c, '#9aa0a6', -54, -76, 108, 3);
    for (let i = 0; i < 18; i++) line(c, '#9aa0a6', 0.4, [-54 + i * 6, -76, -51 + i * 6, -73]);
    for (const x of [-54, 51]) rect(c, '#9aa0a6', x, -76, 3, 76);
    for (let i = 0; i < 5; i++) block(c, -40 + i * 20, -73, 5, 4, '#2a2e34');
    // Speakers.
    for (const x of [-50, 40]) {
      block(c, x, -26, 10, 22, '#1b1d24', '#2a2e34');
      ellipse(c, '#3a3e44', x + 5, -19, 3.4, 3.4);
      ellipse(c, '#3a3e44', x + 5, -9, 2.4, 2.4);
    }
    // Platform: front lip below the anchor (the singer stands on it).
    block(c, -54, -2, 108, 14, '#7a5030', '#9a6a44', '#5a3a20');
    for (let i = 0; i < 9; i++) rect(c, 'rgba(0,0,0,0.15)', -50 + i * 12, -1, 0.6, 12);
    rect(c, '#c8202a', -54, 9, 108, 3);
    if (night && open) rect(c, 'rgba(255,240,200,0.1)', -54, -2, 108, 12);
  });
  if (!open) return;
  // Mic stand.
  const x = d.x, y = d.y;
  line(c, '#3a3e44', 0.7, [x - 8, y + 9, x - 8, y - 6]);
  ellipse(c, '#1b1d24', x - 8, y - 7, 1.2, 1.2);
  if (!isNight()) return;
  // Moving spotlights.
  c.globalCompositeOperation = 'lighter';
  const cols = ['rgba(255,120,200,0.16)', 'rgba(120,200,255,0.16)', 'rgba(255,230,120,0.16)'];
  for (let i = 0; i < 3; i++) {
    const sx = x - 40 + i * 40, sy = y - 72;
    const a = Math.sin(t * 0.9 + i * 2) * 0.5;
    const ex = x + Math.sin(a) * 50 + (i - 1) * 6, ey = y + 8;
    poly(c, cols[i], [sx - 2, sy, sx + 2, sy, ex + 10, ey, ex - 10, ey]);
  }
  c.globalCompositeOperation = 'source-over';
  // Musical notes floating up.
  for (let k = 0; k < 2; k++) {
    const u = (t * 0.35 + k / 2) % 1;
    c.globalAlpha = 1 - u;
    txt(c, k ? '♫' : '♪', x + 12 + k * 14 + Math.sin(t * 2 + k) * 2, y - 26 - u * 26, 6, '#ffd34e', 900, 'rgba(0,0,0,0.35)');
    c.globalAlpha = 1;
  }
}

function carvedPost(c: Ctx, d: Decor): void {
  art(c, d, 'carved', 18, 62, (c) => {
    shadow(c, 0, 0, 6, 2, 0.3, 40);
    block(c, -5, -54, 10, 54, '#8a5a34', '#a8784a', '#5a3a20');
    // Carved bands: a face-like motif, diamonds, waves.
    for (let k = 0; k < 4; k++) {
      const y = -50 + k * 12;
      rect(c, '#5a3a20', -5, y + 9, 10, 0.8);
      if (k === 0) {
        ellipse(c, '#5a3a20', -2, y + 3, 1, 1);
        ellipse(c, '#5a3a20', 2, y + 3, 1, 1);
        rect(c, '#5a3a20', -1.6, y + 6, 3.2, 0.8);
      } else if (k % 2) poly(c, '#c8202a', [0, y + 1, 3.6, y + 4.5, 0, y + 8, -3.6, y + 4.5]);
      else for (let i = 0; i < 3; i++) line(c, '#1b1d24', 0.6, [-4 + i * 3, y + 6, -2.5 + i * 3, y + 2, -1 + i * 3, y + 6]);
    }
    // Feather crest.
    for (let f = -2; f <= 2; f++) line(c, f % 2 ? '#e8473a' : '#ffffff', 1, [f * 0.8, -54, f * 2, -62 + Math.abs(f)]);
  }, false);
}

const CROWD: CharLook[] = [
  { skin: '#f3cba6', hair: '#2a1d16', hairStyle: 'long', top: '#ff9fc0', topShade: '#d97a9a', bottom: '#3a8fd9', shoes: '#ffffff' },
  { skin: '#e9bd96', hair: '#1c1814', hairStyle: 'short', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#2f3442', shoes: '#1d1814' },
  { skin: '#f0c49c', hair: '#3a2418', hairStyle: 'pony', top: '#ffd34e', topShade: '#d9a82a', bottom: '#2f3442', shoes: '#f4f1ea', small: true },
  { skin: '#d9a47a', hair: '#e8e4da', hairStyle: 'bun', top: '#57a06a', topShade: '#3f7f52', bottom: '#5b4636', shoes: '#2b1d14', glasses: true },
  { skin: '#f8d6b8', hair: '#6b3f22', hairStyle: 'cap', hat: '#e2493b', top: '#f4f1ea', topShade: '#d8d2c4', bottom: '#2a2f3d', shoes: '#e2493b', small: true },
  { skin: '#e2b089', hair: '#141218', hairStyle: 'neat', top: '#7d5aa8', topShade: '#5e4282', bottom: '#1d2028', shoes: '#1d1814' },
];

/** Shoppers milling around in the evening (nobody there by day). */
function crowd(c: Ctx, d: Decor, t: number): void {
  if (!marketOpen()) return;
  const v = d.variant ?? 0;
  const spots: [number, number][] = [[-18, -2], [4, 3], [20, -4]];
  spots.forEach(([dx, dy], i) => {
    const look = CROWD[(v * 2 + i) % CROWD.length];
    const ph = t * 0.4 + v + i * 1.7;
    const walk = Math.sin(ph) > 0.6;
    const pose: Pose = {
      facing: (['down', 'left', 'right', 'up'] as const)[(v + i + Math.floor(ph / 3)) % 4],
      phase: (t * 1.6 + i * 0.3) % 1,
      moving: walk,
      blink: (t * 0.7 + i) % 4 < 0.12,
      breath: (t * 0.5 + i * 0.3) % 1,
    };
    drawCharacter(c, look, d.x + dx + Math.sin(ph) * 3, d.y + dy, pose);
  });
}

function marketMap(c: Ctx, r: Rect): void {
  artObj(c, r, 'marketMap', 36, 44, (c) => {
    shadow(c, 0, 0, 14, 2.4, 0.3);
    for (const x of [-12, 10]) block(c, x, -16, 2.4, 16, '#55606a');
    block(c, -15, -38, 30, 24, '#c0392b', '#e05a48', '#8a2a1e');
    block(c, -13.4, -34, 26.8, 18.6, '#f4ecd8', '#fffaf0');
    txt(c, '東大門夜市', 0, -36, 3.2, '#ffd34e', 900);
    // Four coloured zones and the stage.
    const z: [number, number, string][] = [[-12, -33, '#1f4a7a'], [1, -33, '#c8202a'], [-12, -25, '#d0602a'], [1, -25, '#7d5aa8']];
    for (const [x, y, col] of z) block(c, x, y, 11, 7, col);
    txt(c, '外省', -6.5, -29.6, 2.6, '#ffffff');
    txt(c, '原住民', 6.5, -29.6, 2.4, '#ffffff');
    txt(c, '福町', -6.5, -21.6, 2.6, '#ffffff');
    txt(c, '舞台', 6.5, -21.6, 2.6, '#ffffff');
    txt(c, '17:00–24:00', 0, -16, 2.6, '#1b1d24', 800);
  });
}

// ---------------------------------------------------------------------------
// The seaside hotel, Beibin Park, the coast
// ---------------------------------------------------------------------------

function hotel(c: Ctx, night: boolean): void {
  const W = 144, L = -W / 2, R = W / 2;
  shadow(c, 0, 0, W / 2, 4, 0.3);
  // Flat roof edge with a big rooftop sign.
  block(c, L + 2, -132, W - 4, 16, '#cfd6da', '#e8ecee', '#a8b0b6');
  block(c, -40, -138, 80, 13, '#ffffff', '#ffffff', '#c8ccd0');
  txt(c, '海景飯店', 0, -131.6, 9.4, '#1f6f9a', 900);
  if (night) {
    c.globalCompositeOperation = 'lighter';
    ellipse(c, 'rgba(120,220,255,0.3)', 0, -132, 40, 7);
    c.globalCompositeOperation = 'source-over';
  }
  // Tower: white with teal balconies, six floors.
  block(c, L + 4, -118, W - 8, 92, '#f6f8f8', '#ffffff', '#d4dadc');
  for (let f = 0; f < 6; f++) {
    const y = -114 + f * 15;
    for (let i = 0; i < 6; i++) {
      const x = L + 12 + i * 21;
      pane(c, x, y, 16, 9, night, f * 10 + i + 200, '#e8ecec');
      // Balcony glass and rail.
      rect(c, 'rgba(90,190,200,0.55)', x - 1, y + 7, 18, 4);
      rect(c, '#2f8fa8', x - 1, y + 7, 18, 0.8);
    }
  }
  // Wave stripe and the English name.
  c.strokeStyle = '#2f8fa8';
  c.lineWidth = 2;
  c.beginPath();
  for (let x = L + 4; x <= R - 4; x += 2) c.lineTo(x, -24 - Math.sin(x / 8) * 1.6);
  c.stroke();
  txt(c, 'SEAVIEW HOTEL', 0, -29, 4, '#2f6f8a', 800);
  // Lobby: glass front with the door in the middle, a canopy over it.
  block(c, L + 4, -21, W - 8, 21, '#e4e8e8', '#ffffff');
  for (let i = 0; i < 8; i++) {
    const x = L + 8 + i * 17;
    if (Math.abs(x + 8) < 14) continue;
    pane(c, x, -18, 14, 15, night, i + 300, '#c8d0d4');
  }
  vgrad(c, -10, -18, 20, 18, night ? '#ffe7b0' : '#9cc4dc', night ? '#f2c070' : '#4a7494');
  rect(c, '#9aa6ae', -0.4, -18, 0.8, 18);
  rect(c, 'rgba(255,255,255,0.35)', -8, -17, 2, 14);
  block(c, -18, -24, 36, 4, '#2f8fa8', '#4aaac0', '#1f6a80');
  // Vertical neon sign on the corner.
  block(c, R - 14, -110, 10, 44, '#1f4a6a', '#2f6a8a', '#123048');
  vtxt(c, '海景飯店', R - 9, -104, 7, night ? '#9af0ff' : '#e8f8ff', 1.4, 'rgba(0,0,0,0.4)');
  // Planters at the door.
  for (const x of [-26, 20]) {
    block(c, x, -6, 6, 6, '#8a6a4a');
    ellipse(c, '#3f8a4a', x + 3, -7, 4, 3);
  }
}

/** Coconut palm (coast, station plaza). */
function palm(c: Ctx, d: Decor, t: number): void {
  const v = (d.variant ?? 0) % 3;
  art(c, d, 'palm', 56, 86, (c) => {
    shadow(c, 0, 0, 10, 3, 0.3, 60);
    const lean = [6, -5, 2][v];
    // A gently curving, tapering trunk with ring scars.
    const at = (u: number) => [lean * u * u * 2, -u * 66] as const;
    for (let k = 0; k < 12; k++) {
      const [x0, y0] = at(k / 12), [x1, y1] = at((k + 1) / 12);
      line(c, '#7a5a3a', 3.6 - (k / 12) * 1.4, [x0, y0, x1, y1]);
      line(c, '#a8845a', 1.2 - (k / 12) * 0.4, [x0 - 0.6, y0, x1 - 0.6, y1]);
      rect(c, 'rgba(60,40,20,0.45)', x1 - 1.6, y1, 3.2, 0.6);
    }
    // Coconuts.
    for (const dx of [-2, 1.4, 0]) ellipse(c, '#6a5a2a', lean * 2 + dx, -64 + (dx === 0 ? 2 : 0), 1.6, 1.6);
  }, false);
  // Fronds sway live (cheap: a few curves).
  const lean = [6, -5, 2][v];
  const tx = d.x + lean * 2, ty = d.y - 67;
  const sw = wind.at(d.x, d.y) * 2 + Math.sin(t * 1.3 + d.x) * 0.8;
  c.lineCap = 'round';
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI + (i / 6) * Math.PI + (i % 2 ? 0.15 : -0.1);
    const len = 22 - Math.abs(i - 3) * 1.5;
    const ex = tx + Math.cos(a) * len + sw, ey = ty + Math.sin(a) * len * 0.45 + 8;
    c.strokeStyle = i % 2 ? '#3f8a3a' : '#2f7a34';
    c.lineWidth = 2.4;
    c.beginPath();
    c.moveTo(tx, ty);
    c.quadraticCurveTo((tx + ex) / 2 + sw * 0.5, Math.min(ty, ey) - 7, ex, ey);
    c.stroke();
  }
}

/** Betel-nut palm: a very straight, thin ringed trunk with a small crown. */
function betel(c: Ctx, d: Decor, t: number): void {
  const v = (d.variant ?? 0) % 3;
  const hgt = [70, 62, 76][v];
  art(c, d, 'betel', 40, 90, (c) => {
    shadow(c, 0, 0, 6, 2, 0.28, 60);
    rect(c, '#8a8a6a', -1.2, -hgt, 2.4, hgt);
    for (let y = -hgt + 3; y < 0; y += 4) rect(c, 'rgba(60,60,40,0.5)', -1.2, y, 2.4, 0.6);
    rect(c, '#6f9a4a', -1.4, -hgt - 6, 2.8, 8);
    // A cluster of green nuts under the crown.
    for (let k = 0; k < 5; k++) ellipse(c, '#5a8a2a', -2 + (k % 3) * 2, -hgt + 4 + Math.floor(k / 3) * 2, 1.2, 1.2);
  }, false);
  const tx = d.x, ty = d.y - hgt - 4;
  const sw = wind.at(d.x, d.y) * 1.6 + Math.sin(t * 1.5 + d.x) * 0.6;
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI + (i / 5) * Math.PI;
    const len = 13 - Math.abs(i - 2.5) * 1.2;
    const ex = tx + Math.cos(a) * len + sw, ey = ty + Math.sin(a) * len * 0.5 + 5;
    c.strokeStyle = i % 2 ? '#4f9a3a' : '#3a8034';
    c.lineWidth = 1.8;
    c.beginPath();
    c.moveTo(tx, ty);
    c.quadraticCurveTo((tx + ex) / 2, Math.min(ty, ey) - 5, ex, ey);
    c.stroke();
  }
}

function bench(c: Ctx, d: Decor): void {
  art(c, d, 'bench', 30, 16, (c) => {
    shadow(c, 0, 0, 13, 2, 0.3);
    block(c, -13, -8, 26, 3, '#a8784a', '#c8986a');
    block(c, -13, -13, 26, 3, '#a8784a', '#c8986a');
    for (const x of [-11, 9]) rect(c, '#3a3e44', x, -13, 2, 13);
  }, false);
}

/** White seafront railing with a blue wave pattern. */
function railing(c: Ctx, d: Decor): void {
  const w = d.w ?? 30;
  art(c, d, `rail${w}`, w + 4, 16, (c) => {
    for (let x = -w / 2; x <= w / 2; x += 6) rect(c, '#e8ecef', x - 0.6, -10, 1.6, 10);
    rect(c, '#ffffff', -w / 2, -11, w, 1.6);
    c.strokeStyle = '#2f8fa8';
    c.lineWidth = 0.8;
    c.beginPath();
    for (let x = -w / 2; x <= w / 2; x += 1) c.lineTo(x, -5 - Math.sin(x / 2.5) * 1.6);
    c.stroke();
  }, false);
}

function bikeSign(c: Ctx, d: Decor): void {
  art(c, d, 'bikeSign', 30, 40, (c) => {
    shadow(c, 0, 0, 4, 1.4);
    rect(c, '#55606a', -0.8, -30, 1.6, 30);
    block(c, -12, -38, 24, 12, '#2f6fa8', '#4a8ac8');
    // Bicycle pictogram.
    for (const x of [-7, -2]) {
      c.strokeStyle = '#ffffff';
      c.lineWidth = 0.6;
      c.beginPath();
      c.arc(x, -30, 2, 0, Math.PI * 2);
      c.stroke();
    }
    line(c, '#ffffff', 0.6, [-7, -30, -4.6, -33, -2, -30]);
    txt(c, '自行車道', 5.6, -34, 3, '#ffffff', 900);
    txt(c, 'BIKE PATH', 5.6, -30, 2.2, '#cfe6f2', 800);
  }, false);
}

/** Coin-operated binoculars on a short post (river-mouth lookout, seafront). */
function binoculars(c: Ctx, r: Rect, label: string, en: string): void {
  artObj(c, r, `bino${label}`, 44, 40, (c) => {
    shadow(c, 0, 0, 16, 2.4, 0.3);
    // Railing.
    for (let x = -18; x <= 18; x += 6) rect(c, '#e8ecef', x - 0.6, -10, 1.6, 10);
    rect(c, '#ffffff', -19, -11, 38, 1.6);
    // Binoculars.
    rect(c, '#55606a', -1, -20, 2, 20);
    block(c, -6, -27, 12, 7, '#2f8fa8', '#4aaac0', '#1f6a80');
    for (const x of [-4, 2]) ellipse(c, '#1b2430', x + 1, -23.6, 1.6, 1.6);
    // Info plate.
    block(c, 7, -22, 12, 9, '#f4f1ea', '#ffffff');
    txt(c, label, 13, -19.6, 2.6, '#1f4a6a', 900);
    txt(c, en, 13, -16, 1.6, '#55606a', 700);
  });
}

// ---------------------------------------------------------------------------
// Ji'an: Container Coffee, Qingxiu Temple, the village
// ---------------------------------------------------------------------------

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  depth: number;
  col: string;
  end?: boolean;
}

/** One shipping container in 3/4 view: front face, top face, corrugation, optional glass end. */
function container(c: Ctx, b: Box, night: boolean): void {
  const { x, y, w, h, depth, col } = b;
  // Top face.
  poly(c, shade(col, 0.12), [x, y, x + 4, y - depth, x + w + 4, y - depth, x + w, y]);
  // Right side face (a sliver).
  poly(c, shade(col, -0.25), [x + w, y, x + w + 4, y - depth, x + w + 4, y + h - depth, x + w, y + h]);
  // Front face.
  rect(c, shade(col, -0.45), x - 0.4, y - 0.4, w + 0.8, h + 0.8);
  rect(c, col, x, y, w, h);
  if (b.end) {
    // Glass end with a frame: café interior.
    rect(c, shade(col, -0.2), x + 1.4, y + 1.4, w - 2.8, h - 2.8);
    if (night) vgrad(c, x + 2.4, y + 2.4, w - 4.8, h - 4.8, '#ffe7b0', '#f2b860');
    else vgrad(c, x + 2.4, y + 2.4, w - 4.8, h - 4.8, '#a8d4e8', '#4a7a9a');
    rect(c, 'rgba(255,255,255,0.4)', x + 3, y + 3, 1.6, h - 6);
    rect(c, 'rgba(0,0,0,0.25)', x + w / 2 - 0.3, y + 2.4, 0.6, h - 4.8);
  } else {
    for (let i = 2; i < w - 1; i += 2.4) rect(c, 'rgba(0,0,0,0.09)', x + i, y + 1, 0.7, h - 2);
    rect(c, 'rgba(255,255,255,0.25)', x, y, w, 0.7);
    rect(c, 'rgba(0,0,0,0.2)', x, y + h - 1, w, 1);
  }
  // Corner posts.
  rect(c, shade(col, -0.35), x, y, 1, h);
  rect(c, shade(col, -0.35), x + w - 1, y, 1, h);
}

function containerCafe(c: Ctx, night: boolean): void {
  const W = 224, L = -W / 2;
  shadow(c, 0, 0, W / 2, 5, 0.32);
  const WHITE = '#f4f5f2', GREEN = '#2f7a5a', MINT = '#9ad8b8', GREY = '#cfd6da', YEL = '#f2c94c';
  // Level 0: containers lying N–S show their glass ends to the plaza; a few E–W ones between.
  const boxes: Box[] = [];
  const lv = (k: number) => -22 - k * 26;
  // Back rows on each level (drawn first, higher up the screen) give the block its bulk.
  const back = (k: number) => {
    for (let i = 0; i < 6; i++) boxes.push({ x: L + 8 + i * 34, y: lv(k) - 10, w: 30, h: 22, depth: 6, col: (i + k) % 3 === 0 ? GREY : (i + k) % 3 === 1 ? WHITE : '#e4ece8' });
  };
  back(0);
  // Front row, ground level.
  const front: [number, number, string, boolean][] = [
    [L + 4, 20, WHITE, true], [L + 26, 44, GREEN, false], [L + 72, 20, WHITE, true], [L + 94, 20, MINT, true],
    [L + 116, 44, WHITE, false], [L + 162, 20, GREEN, true], [L + 184, 36, WHITE, false],
  ];
  for (const [x, w, col, end] of front) boxes.push({ x, y: lv(0), w, h: 22, depth: 8, col, end });
  // Level 1: crosswise and sticking out beyond the base (cantilevers).
  const l1: [number, number, string, boolean][] = [
    [L - 6, 54, WHITE, false], [L + 50, 20, GREEN, true], [L + 72, 56, WHITE, false], [L + 130, 20, YEL, true],
    [L + 152, 22, WHITE, true], [L + 176, 56, MINT, false],
  ];
  back(1);
  for (const [x, w, col, end] of l1) boxes.push({ x, y: lv(1), w, h: 22, depth: 8, col, end });
  // Level 2: a few long ones on top, one leaning out over the entrance.
  const l2: [number, number, string, boolean][] = [
    [L + 16, 60, GREEN, false], [L + 82, 22, WHITE, true], [L + 106, 64, WHITE, false], [L + 172, 22, GREEN, true],
  ];
  back(2);
  for (const [x, w, col, end] of l2) boxes.push({ x, y: lv(2), w, h: 22, depth: 8, col, end });
  boxes.push({ x: L + 60, y: lv(3) + 4, w: 46, h: 18, depth: 6, col: MINT });
  boxes.push({ x: L + 130, y: lv(3) + 4, w: 22, h: 18, depth: 6, col: WHITE, end: true });
  for (const b of boxes) container(c, b, night);
  // Shadows under the cantilevers.
  rect(c, 'rgba(0,0,0,0.12)', L + 4, lv(0), W - 8, 2);
  // Entrance: glass doors in the middle of the ground floor.
  vgrad(c, -10, -20, 20, 20, night ? '#ffe7b0' : '#9cc4dc', night ? '#f2c070' : '#4a7494');
  rect(c, '#3a3e44', -10.6, -20.6, 21.2, 0.8);
  rect(c, '#3a3e44', -0.3, -20, 0.6, 20);
  // The sign: an original cup-with-a-wave logo.
  const sx = 46, sy = -62;
  block(c, sx - 30, sy - 9, 60, 16, GREEN, '#3f9a72', '#1f5a40');
  ellipse(c, '#ffffff', sx - 22, sy - 1, 5.4, 5.4);
  ellipse(c, GREEN, sx - 22, sy - 1, 4.4, 4.4);
  block(c, sx - 25, sy - 3, 6, 5, '#ffffff');
  c.strokeStyle = '#ffffff';
  c.lineWidth = 0.6;
  c.beginPath();
  c.arc(sx - 18.4, sy - 0.6, 1.4, -Math.PI / 2, Math.PI / 2);
  c.stroke();
  line(c, GREEN, 0.6, [sx - 25, sy - 1.4, sx - 23.5, sy - 2.4, sx - 22, sy - 1.4, sx - 20.5, sy - 2.4, sx - 19, sy - 1.4]);
  txt(c, 'Container Coffee', sx + 5, sy - 3.4, 5.4, '#ffffff', 800);
  txt(c, '貨櫃咖啡', sx + 5, sy + 3.2, 5.4, '#e8f6ee', 900);
  if (night) {
    c.globalCompositeOperation = 'lighter';
    ellipse(c, 'rgba(160,255,200,0.2)', sx, sy, 36, 10);
    c.globalCompositeOperation = 'source-over';
  }
  // Rooftop plants on the top container.
  for (let i = 0; i < 5; i++) ellipse(c, '#4f9a4a', L + 66 + i * 9, lv(3) + 1, 3, 2);
}

function umbrellas(c: Ctx, d: Decor): void {
  art(c, d, 'umb', 40, 34, (c) => {
    shadow(c, 0, 0, 16, 2.6, 0.28);
    block(c, -8, -9, 16, 3, '#f4f1ea', '#ffffff');
    rect(c, '#9a948a', -1, -6, 2, 6);
    for (const x of [-12, 10]) block(c, x, -6, 3, 6, '#2f7a5a');
    rect(c, '#9a948a', -0.4, -26, 0.8, 18);
    const col = (d.variant ?? 0) ? '#2f7a5a' : '#f4f5f2';
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(-16, -22);
    c.quadraticCurveTo(0, -34, 16, -22);
    c.closePath();
    c.fill();
    for (let i = -12; i <= 12; i += 8) line(c, (d.variant ?? 0) ? '#f4f5f2' : '#2f7a5a', 0.6, [0, -30, i, -22]);
  }, false);
}

function photoFrame(c: Ctx, r: Rect): void {
  artObj(c, r, 'photo', 40, 46, (c) => {
    shadow(c, 0, 0, 14, 2.6, 0.3);
    for (const x of [-12, 10]) block(c, x, -40, 2.4, 40, '#2f7a5a');
    c.strokeStyle = '#2f7a5a';
    c.lineWidth = 2.4;
    c.strokeRect(-12, -40, 24, 22);
    rect(c, '#f4f5f2', -14, -46, 28, 7);
    txt(c, 'PHOTO SPOT', 0, -42.6, 3, '#2f7a5a', 900);
    txt(c, '📷', 0, -12, 6, '#1b1d24', 400);
    txt(c, "Ji'an 吉安", 0, -16.4, 2.4, '#2f7a5a', 800);
  });
}

function cafeMenu(c: Ctx, r: Rect): void {
  artObj(c, r, 'cafeMenu', 30, 36, (c) => {
    shadow(c, 0, 0, 9, 2, 0.3);
    poly(c, '#5a3a20', [-8, 0, -5, -26, 5, -26, 8, 0, 6.4, 0, 3.8, -24, -3.8, -24, -6.4, 0]);
    block(c, -7, -24, 14, 20, '#1f2a24', '#2a3a30');
    txt(c, 'MENU', 0, -21, 2.8, '#ffffff', 900);
    const items = ['Latte', 'Hot choc', 'Tea', 'Cake'];
    items.forEach((s, i) => {
      txt(c, s, -1.4, -17 + i * 3.4, 1.9, '#e8f6ee', 700);
      rect(c, '#9ad8b8', 3.6, -17.6 + i * 3.4, 1.6, 1.2);
    });
  });
}

/** Qingxiu Temple's wooden hall on a stone base, with a grey tiled roof. */
function templeHall(c: Ctx, night: boolean): void {
  const W = 112, L = -W / 2, R = W / 2;
  shadow(c, 0, 0, W / 2, 4, 0.3);
  // Roof: big sweeping eaves, grey tiles, dark ridge with raised ends.
  c.fillStyle = '#4a5058';
  c.beginPath();
  c.moveTo(L - 8, -44);
  c.quadraticCurveTo(L + 6, -46, L + 14, -70);
  c.lineTo(R - 14, -70);
  c.quadraticCurveTo(R - 6, -46, R + 8, -44);
  c.lineTo(R + 4, -40);
  c.lineTo(L - 4, -40);
  c.closePath();
  c.fill();
  for (let i = 0; i < 26; i++) {
    const x = L + 4 + i * 4;
    line(c, 'rgba(255,255,255,0.12)', 0.6, [x, -68, x + (x < 0 ? -3 : 3), -42]);
  }
  rect(c, '#2a3036', L + 12, -73, W - 24, 4);
  for (const x of [L + 10, R - 16]) poly(c, '#2a3036', [x, -69, x + 3, -78, x + 6, -69]);
  // A small gable over the front (kara-hafu style) with a gold crest.
  poly(c, '#3e444c', [-18, -44, 0, -56, 18, -44]);
  rect(c, '#f2ead8', -10, -49, 20, 4);
  ellipse(c, '#d9b450', 0, -50, 1.6, 1.6);
  rect(c, '#2a3036', L - 4, -41, W + 8, 2);
  // Wooden walls: dark posts, lattice doors, white plaster panels.
  block(c, L + 4, -39, W - 8, 30, '#6a4428', '#8a5a34', '#4a2e18');
  for (let i = 0; i < 7; i++) {
    const x = L + 8 + i * 14.4;
    rect(c, '#3a2414', x, -39, 2, 30);
    if (i === 6) break;
    const doors = i >= 2 && i <= 3;
    rect(c, doors ? (night ? '#ffe2a0' : '#e8dcc0') : '#f2ead8', x + 2.6, -36, 11, doors ? 26 : 12);
    if (doors) for (let k = 0; k < 5; k++) rect(c, '#6a4428', x + 2.6, -33 + k * 5, 11, 0.5);
    for (let k = 1; k < 3; k++) rect(c, '#6a4428', x + 2.6 + k * 3.7, -36, 0.5, doors ? 26 : 12);
  }
  // Wooden verandah and stone base with steps.
  block(c, L, -10, W, 4, '#8a5a34', '#a8784a');
  block(c, L + 2, -6, W - 4, 6, '#9a948a', '#b8b2a6', '#7a746a');
  for (let k = 0; k < 3; k++) rect(c, '#b8b2a6', -14 + k * 2, -6 + k * 2, 28 - k * 4, 2);
  // Name plaque.
  block(c, -12, -45, 24, 6, '#2a1810');
  txt(c, '慶修院', 0, -42, 4, '#f4e6c0', 900);
  // Hanging bell rope.
  line(c, '#c8202a', 1, [6, -38, 6, -20]);
  ellipse(c, '#d9b450', 6, -38, 2, 2);
}

/** A row of eleven small seated stone Buddhas on pedestals. */
function buddhas(c: Ctx, d: Decor): void {
  art(c, d, 'buddhas', 58, 22, (c) => {
    for (let i = 0; i < 11; i++) {
      const x = -26 + i * 5.2;
      const v = rnd(i, d.variant ?? 0);
      shadow(c, x, 0, 2.4, 0.8, 0.25, 8);
      block(c, x - 2, -3, 4, 3, '#8a8680');
      const col = v < 0.3 ? '#9a968e' : v < 0.7 ? '#a8a49a' : '#8e8a82';
      ellipse(c, col, x, -5.6, 2.2, 2.4);
      ellipse(c, shade(col, 0.1), x, -9, 1.4, 1.4);
      rect(c, 'rgba(255,255,255,0.2)', x - 1, -10, 0.6, 1);
      if (v > 0.55) rect(c, '#c8202a', x - 1.6, -7.6, 3.2, 1.6);
      if (v < 0.2) ellipse(c, '#5f8a4a', x + 2, -1, 1.2, 0.6);
    }
  }, false);
}

/** Stone lantern (tōrō), glowing softly at night. */
function toro(c: Ctx, d: Decor): void {
  art(c, d, 'toro', 14, 28, (c, night) => {
    shadow(c, 0, 0, 5, 1.6, 0.3, 20);
    block(c, -4, -3, 8, 3, '#8a8680');
    rect(c, '#9a968e', -1.4, -12, 2.8, 9);
    block(c, -4, -14, 8, 2, '#8a8680');
    block(c, -3, -19, 6, 5, '#a8a49a');
    rect(c, night ? '#ffd88a' : '#3a3630', -1.6, -18, 3.2, 3);
    poly(c, '#8a8680', [-6, -19, 6, -19, 2, -23, -2, -23]);
    ellipse(c, '#8a8680', 0, -24, 1.2, 1.2);
  });
}

function basin(c: Ctx, d: Decor): void {
  art(c, d, 'basin', 28, 34, (c) => {
    shadow(c, 0, 0, 11, 2.4, 0.3);
    for (const x of [-11, 9]) rect(c, '#5a3a20', x, -24, 2, 24);
    poly(c, '#4a5058', [-14, -24, 14, -24, 10, -30, -10, -30]);
    block(c, -8, -8, 16, 8, '#9a968e', '#b8b2a6');
    rect(c, '#7fb8d4', -6.6, -7.6, 13.2, 2);
    line(c, '#c8a060', 0.8, [-4, -10, 4, -12]);
    line(c, '#c8a060', 0.8, [4, -12, 4, -10]);
  }, false);
}

function offeringBox(c: Ctx, r: Rect): void {
  artObj(c, r, 'offering', 40, 30, (c) => {
    shadow(c, 0, 0, 16, 2.4, 0.3);
    block(c, -15, -12, 30, 12, '#6a4428', '#8a5a34', '#4a2e18');
    for (let k = 0; k < 6; k++) rect(c, '#3a2414', -13 + k * 5, -11, 0.8, 4);
    txt(c, '奉納', 0, -5, 4.2, '#f4e6c0', 900);
    rect(c, '#d9b450', -15, -12.6, 30, 0.8);
  });
}

function buddhaBoard(c: Ctx, r: Rect): void {
  artObj(c, r, 'buddhaBoard', 30, 36, (c) => {
    shadow(c, 0, 0, 10, 2, 0.3);
    for (const x of [-9, 7]) rect(c, '#5a3a20', x, -18, 2, 18);
    block(c, -11, -30, 22, 14, '#6a4428', '#8a5a34');
    txt(c, '八十八', 0, -26, 4, '#f4e6c0', 900);
    txt(c, '石佛', 0, -21.4, 4, '#f4e6c0', 900);
  });
}

function paddySign(c: Ctx, r: Rect): void {
  artObj(c, r, 'paddySign', 30, 32, (c) => {
    shadow(c, 0, 0, 8, 1.6, 0.3);
    rect(c, '#6a4a2a', -0.8, -20, 1.6, 20);
    block(c, -10, -28, 20, 10, '#e8dcc0', '#f6ecd8');
    txt(c, '吉安好米', 0, -24.6, 3.6, '#3a6a2a', 900);
    txt(c, "JI'AN RICE", 0, -20.8, 2, '#5a4a2a', 800);
  });
}

function scarecrow(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  const sw = Math.sin(t * 1.4 + x) * 0.6;
  shadow(c, x, y, 4, 1.2);
  rect(c, '#6a4a2a', x - 0.6, y - 20, 1.2, 20);
  line(c, '#6a4a2a', 1.2, [x - 8, y - 15 + sw, x + 8, y - 15 - sw]);
  block(c, x - 4, y - 18, 8, 9, '#3a8fd9', '#5aa8e8');
  ellipse(c, '#e8d8a0', x, y - 21, 3, 3);
  poly(c, '#d9b450', [x - 6, y - 22, x + 6, y - 22, x, y - 27]);
}

/** A little egret wading in the paddy, dipping its beak now and then. */
function egret(c: Ctx, d: Decor, t: number): void {
  const x = d.x + Math.sin(t * 0.2 + (d.variant ?? 0) * 3) * 6, y = d.y;
  const dip = Math.max(0, Math.sin(t * 0.9 + (d.variant ?? 0) * 2)) ** 8;
  const f = Math.cos(t * 0.2 + (d.variant ?? 0) * 3) > 0 ? 1 : -1;
  ellipse(c, 'rgba(0,0,0,0.15)', x, y, 3, 0.8);
  line(c, '#2a2a2a', 0.5, [x - 0.6, y, x - 0.6, y - 4]);
  line(c, '#2a2a2a', 0.5, [x + 0.6, y, x + 0.6, y - 4]);
  ellipse(c, '#ffffff', x, y - 6, 3.2, 2);
  const hx = x + f * (2.4 + dip * 1.6), hy = y - 9 + dip * 5;
  line(c, '#ffffff', 1, [x + f * 1.4, y - 7, hx, hy]);
  ellipse(c, '#ffffff', hx, hy, 1.2, 1);
  line(c, '#ffd34e', 0.6, [hx + f * 0.8, hy, hx + f * 3, hy + 0.6]);
}

const HOUSE_COLS = ['#e8e0d0', '#d8e0e4', '#f0e0c8', '#e4d8e8', '#dfe8d8'];

/** A low Ji'an house: tiled facade, iron gate, a red door couplet, maybe a rooftop add-on. */
function house(c: Ctx, d: Decor): void {
  const v = d.variant ?? 0;
  art(c, d, 'house', 96, 70, (c, night) => {
    const col = HOUSE_COLS[v % HOUSE_COLS.length];
    shadow(c, 0, 0, 46, 3, 0.3);
    const two = v % 2 === 1;
    const top = two ? -60 : -44;
    if (!two) {
      // A sloping red-tile roof on single-storey houses.
      poly(c, '#a8503a', [-48, -40, -42, -62, 42, -62, 48, -40]);
      for (let k = 0; k < 7; k++) line(c, 'rgba(60,20,10,0.3)', 0.5, [-46 + k, -42 - k * 3, 46 - k, -42 - k * 3]);
      rect(c, '#7a3424', -42, -63, 84, 2);
    } else {
      block(c, 18, top - 10, 22, 10, '#7f9aa8', '#a8c0cc', '#5a7480');
      block(c, -36, top - 9, 9, 8, '#e8e4da', '#ffffff');
    }
    block(c, -46, two ? top : -42, 92, two ? -top : 42, col, shade(col, 0.15), shade(col, -0.2));
    for (let y = (two ? top : -42) + 3; y < -2; y += 2.6) rect(c, 'rgba(0,0,0,0.05)', -45, y, 90, 0.4);
    if (two) for (let i = 0; i < 3; i++) {
      pane(c, -38 + i * 28, -50, 20, 10, night, v * 9 + i);
      for (let k = 0; k < 6; k++) rect(c, 'rgba(60,60,60,0.5)', -38 + i * 28 + k * 4, -51, 0.5, 12);
    }
    // Ground floor: a roll-up shutter garage (scooters live here), the front door, a window.
    block(c, -40, -26, 30, 26, '#b8c0c6', '#d8dee2', '#8a9096');
    for (let k = 0; k < 9; k++) rect(c, 'rgba(0,0,0,0.12)', -40, -24 + k * 2.8, 30, 0.6);
    block(c, -2, -24, 14, 24, '#a8302a', '#c84a3a', '#7a1a14');
    rect(c, '#d9b450', 8.6, -12, 1.2, 1.2);
    for (const x of [-5, 13.4]) block(c, x, -24, 2.4, 13, '#e8473a');
    block(c, 1, -30, 8, 4, '#e8473a');
    txt(c, '福', 5, -28, 3, '#ffd34e', 900);
    pane(c, 20, -22, 20, 12, night, v * 7 + 3);
    for (let k = 0; k < 6; k++) rect(c, 'rgba(60,60,60,0.55)', 20 + k * 4, -23, 0.5, 14);
    // A red altar lamp glow inside at night.
    if (night) ellipse(c, 'rgba(255,90,60,0.35)', 30, -16, 6, 4);
    rect(c, '#9a948a', -46, -1.4, 92, 1.4);
  });
}

/** “Good Neighbor” (好鄰居): a generic Taiwanese convenience store. */
function store(c: Ctx, d: Decor): void {
  art(c, d, 'store', 144, 70, (c, night) => {
    shadow(c, 0, 0, 70, 3, 0.3);
    block(c, -70, -62, 140, 62, '#f4f5f2', '#ffffff', '#d4dadc');
    // Teal and yellow stripes with the invented name.
    rect(c, '#2fa3a0', -70, -52, 140, 5);
    rect(c, '#ffd34e', -70, -47, 140, 2.4);
    rect(c, '#2fa3a0', -70, -44.6, 140, 1.2);
    block(c, -40, -62, 80, 9, '#2fa3a0', '#4ac0bc');
    txt(c, '好鄰居 GOOD NEIGHBOR', 0, -57.4, 5.4, '#ffffff', 900);
    // Glass front: shelves, a coffee machine, the counter; posters.
    vgrad(c, -64, -40, 128, 36, night ? '#fff6dc' : '#d8e8f0', night ? '#ffe8b0' : '#a8c8dc');
    for (let i = 0; i < 4; i++) {
      const x = -60 + i * 22;
      for (let k = 0; k < 3; k++) {
        rect(c, '#9aa0a6', x, -34 + k * 9, 18, 0.8);
        for (let j = 0; j < 5; j++) rect(c, ['#e8473a', '#3aa0d9', '#ffd34e', '#57c278', '#ff9fc0'][(i + j + k) % 5], x + 1 + j * 3.4, -38 + k * 9, 2.4, 3.6);
      }
    }
    block(c, 30, -22, 30, 18, '#2fa3a0', '#4ac0bc');
    rect(c, 'rgba(255,255,255,0.35)', -64, -40, 128, 1);
    for (let i = 0; i < 6; i++) rect(c, '#9aa6ae', -64 + i * 25.6, -40, 0.8, 36);
    // Automatic doors.
    rect(c, '#3a3e44', 4, -40, 0.8, 36);
    rect(c, '#3a3e44', 24, -40, 0.8, 36);
    block(c, 6, -44, 16, 3, '#ffd34e');
    txt(c, '24H', 14, -42.6, 2.4, '#1b1d24', 900);
    rect(c, '#9a948a', -70, -4, 140, 4);
  });
}

function waterTower(c: Ctx, d: Decor): void {
  art(c, d, 'wtower', 22, 50, (c) => {
    shadow(c, 0, 0, 7, 2, 0.28, 40);
    for (const x of [-6, 5]) rect(c, '#55606a', x, -30, 1.2, 30);
    line(c, '#55606a', 0.5, [-6, -20, 6, -10]);
    line(c, '#55606a', 0.5, [6, -20, -6, -10]);
    block(c, -8, -44, 16, 14, '#3a8fd9', '#5aa8e8', '#2a6fb0');
    ellipse(c, '#5aa8e8', 0, -44, 8, 2);
  }, false);
}

function tourGroup(c: Ctx, d: Decor, t: number): void {
  // A guide's flag over a little tour group on the snack street (daytime only).
  if (marketOpen() && isNight()) return;
  const x = d.x, y = d.y;
  rect(c, '#55606a', x - 0.4, y - 36, 0.8, 30);
  const fl = Math.sin(t * 3) * 1.2;
  poly(c, '#ffd34e', [x, y - 36, x + 9, y - 34 + fl, x, y - 31]);
  for (let i = 0; i < 3; i++) {
    const look = CROWD[(i + 2) % CROWD.length];
    const pose: Pose = { facing: i === 0 ? 'down' : 'up', phase: 0, moving: false, blink: (t + i) % 5 < 0.1, breath: (t * 0.5 + i * 0.3) % 1 };
    drawCharacter(c, look, x + 2 + i * 9, y + (i % 2) * 3, pose);
  }
}

// ---------------------------------------------------------------------------
// Art pass: station-plaza life, street clutter, cables, sheds, puddles, sitters
// ---------------------------------------------------------------------------

const FACING4 = ['down', 'up', 'left', 'right'] as const;
const SUITCASES = ['#e2493b', '#2f4a8a', '#1b1d24', '#57c278', '#ffd34e', '#c8cdd2', '#7d5aa8'];

/** A traveller standing with a rolling suitcase (`variant` = look*4 + facing). */
function traveler(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const look = CROWD[Math.floor(v / 4) % CROWD.length];
  const facing = FACING4[v % 4];
  const seed = rnd(Math.round(d.x), Math.round(d.y));
  const pose: Pose = { facing, phase: 0, moving: false, blink: (t + seed * 5) % 4 < 0.12, breath: (t * 0.5 + seed) % 1 };
  const side = facing === 'left' ? -1 : 1;
  const sx = d.x + side * 9, col = SUITCASES[Math.floor(v / 4 + seed * 3) % SUITCASES.length];
  if (facing === 'up') suitcase(c, sx, d.y - 1, col);
  drawCharacter(c, look, d.x, d.y, pose);
  if (facing !== 'up') suitcase(c, sx, d.y + 1, col);
}

function suitcase(c: Ctx, x: number, y: number, col: string): void {
  shadow(c, x, y, 4, 1.4);
  rect(c, '#2a2a30', x - 0.6, y - 20, 1.2, 9);
  rect(c, '#2a2a30', x - 2.6, y - 20.6, 5.2, 1.2);
  block(c, x - 3.6, y - 12, 7.2, 11, col, shade(col, 0.3), shade(col, -0.3));
  rect(c, 'rgba(255,255,255,0.3)', x - 2.4, y - 10.4, 1, 7);
  rect(c, 'rgba(0,0,0,0.2)', x - 3, y - 6.2, 6, 0.6);
  ellipse(c, '#1b1d24', x - 2.2, y - 0.4, 1, 1);
  ellipse(c, '#1b1d24', x + 2.2, y - 0.4, 1, 1);
}

/** People queueing for the tourist shuttle, with the stop's pole sign at the head of the line. */
function queue(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 56, n = Math.max(2, Math.round(w / 13));
  const v = d.variant ?? 0;
  // Pole with the route sign.
  const px0 = d.x + w / 2 + 6;
  shadow(c, px0, d.y, 2.6, 1);
  rect(c, '#55606a', px0 - 0.8, d.y - 34, 1.6, 34);
  block(c, px0 - 7, d.y - 42, 14, 10, '#ffffff', '#ffffff', '#c8ccd0');
  rect(c, '#2fa36a', px0 - 6.4, d.y - 41.4, 12.8, 3);
  txt(c, '候車處', px0, d.y - 39.8, 2.6, '#ffffff', 900);
  txt(c, '台灣好行', px0, d.y - 35.6, 2.6, '#1f6a4a', 900);
  for (let i = 0; i < n; i++) {
    const look = CROWD[(i * 2 + v) % CROWD.length];
    const x = d.x - w / 2 + 6 + i * ((w - 12) / Math.max(1, n - 1));
    const y = d.y + ((i + v) % 2) * 2 - 1;
    const seed = rnd(i, v);
    const pose: Pose = { facing: 'right', phase: 0, moving: false, blink: (t * 0.8 + seed * 3) % 4 < 0.12, breath: (t * 0.5 + seed) % 1 };
    if (i % 2 === 0) suitcase(c, x - 7, y + 1, SUITCASES[(i + v) % SUITCASES.length]);
    drawCharacter(c, look, x, y, pose);
  }
}

/** Flame tree (鳳凰木): wide umbrella crown in scarlet, Hualien's summer tree. */
function flamboyant(c: Ctx, d: Decor, t: number): void {
  const sway = wind.at(d.x, d.y) * 1.2;
  art(c, d, 'flamboyant', 76, 62, (c) => {
    shadow(c, 0, 0, 20, 3.6, 0.3, 40);
    block(c, -2.4, -24, 4.8, 24, '#6a4a30', '#8a6a48', '#4a3020');
    line(c, '#6a4a30', 1.6, [0, -22, -12, -34, -22, -38]);
    line(c, '#6a4a30', 1.6, [0, -22, 12, -34, 24, -38]);
    line(c, '#6a4a30', 1.2, [0, -24, 2, -40]);
    const blobs: [number, number, number, number][] = [[-26, -40, 12, 7], [26, -40, 12, 7], [-14, -46, 14, 8], [14, -46, 14, 8], [0, -50, 16, 8], [-30, -34, 9, 5], [30, -34, 9, 5], [0, -42, 20, 7]];
    for (const [bx, by, rx, ry] of blobs) ellipse(c, '#2f6a30', bx, by + 1.2, rx, ry);
    for (const [bx, by, rx, ry] of blobs) ellipse(c, '#4a8a3a', bx, by, rx, ry);
    // Scarlet blossom clusters all over the crown.
    for (let i = 0; i < 46; i++) {
      const a = rnd(i, 3) * Math.PI * 2, r = Math.sqrt(rnd(i, 4));
      const bx = Math.cos(a) * 34 * r, by = -44 + Math.sin(a) * 9 * r;
      ellipse(c, rnd(i, 5) < 0.5 ? '#e8402a' : '#ff6a3a', bx, by, 2.2 + rnd(i, 6) * 1.6, 1.6 + rnd(i, 7));
    }
    for (let i = 0; i < 12; i++) ellipse(c, '#ffb070', (rnd(i, 8) - 0.5) * 60, -46 + (rnd(i, 9) - 0.5) * 12, 0.8, 0.8);
  }, false);
  // A few petals drifting off in the wind (live, cheap).
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.25 + i / 3 + rnd(Math.round(d.x), i)) % 1;
    ellipse(c, `rgba(232,64,42,${0.8 * (1 - k)})`, d.x + 20 - k * 30 + sway * 10 * k + Math.sin(t * 2 + i) * 2, d.y - 44 + k * 40, 1.2, 0.8);
  }
}

/** A round concrete planter overflowing with bedding flowers (`variant` picks the colours). */
function flowerPot(c: Ctx, d: Decor): void {
  art(c, d, 'flowerPot', 30, 22, (c) => {
    const v = d.variant ?? 0;
    const cols = [['#e8473a', '#ffd34e', '#ffffff'], ['#ff7ab0', '#ffffff', '#ffd34e'], ['#ffb43a', '#e8473a', '#ff7ab0'], ['#7d5aa8', '#ffffff', '#ff9fc0']][v % 4];
    shadow(c, 0, 0, 12, 2.6);
    ellipse(c, '#8a857c', 0, -2, 11, 4);
    block(c, -11, -9, 22, 7, '#a8a49c', '#c8c4ba', '#76726c');
    ellipse(c, '#3f8a3a', 0, -10, 11.5, 4);
    ellipse(c, '#57a048', 0, -11, 10, 3.2);
    for (let i = 0; i < 14; i++) {
      const a = rnd(i, v) * Math.PI * 2, r = Math.sqrt(rnd(i, v + 9));
      ellipse(c, cols[i % 3], Math.cos(a) * 9 * r, -11 + Math.sin(a) * 2.6 * r - 1, 1.5, 1.3);
    }
  }, false);
}

const BANNERS: [string, string, string][] = [['花蓮', '#2f6fa8', '#ffffff'], ['太魯閣', '#2f8a5a', '#ffffff'], ['洄瀾', '#e2493b', '#fff6dc'], ['慶修院', '#7d5aa8', '#ffe9b0'], ['松園別館', '#3a6a3a', '#ffffff']];

/** Street lamp with a vertical banner (`variant` picks the text), as along Hualien's avenues. */
function banner(c: Ctx, d: Decor, t: number): void {
  const [text, bg, fg] = BANNERS[(d.variant ?? 0) % BANNERS.length];
  const x = d.x, y = d.y, night = isNight();
  shadow(c, x, y, 4, 1.5);
  rect(c, OUTLINE, x - 1, y - 34, 3, 34);
  rect(c, '#5a6470', x, y - 33, 1, 32);
  rect(c, OUTLINE, x - 6, y - 34, 14, 1.4);
  ellipse(c, night ? '#fff0b0' : '#dfe4e8', x + 7, y - 36, 3, 2.4);
  if (night) ellipse(c, 'rgba(255,230,150,0.28)', x + 7, y - 34, 10, 8);
  // Banner hanging from a short arm.
  const flap = wind.at(x, y) * 1.5 + Math.sin(t * 3 + x) * 0.6;
  rect(c, OUTLINE, x - 8, y - 30, 8, 1);
  rect(c, OUTLINE, x - 8, y - 14, 8, 1);
  poly(c, bg, [x - 7.5, y - 30, x - 0.5, y - 30, x - 0.5 + flap, y - 14, x - 7.5 + flap, y - 14]);
  rect(c, 'rgba(255,255,255,0.25)', x - 7, y - 29.4, 6, 0.6);
  vtxt(c, text, x - 4 + flap * 0.5, y - 26.6, text.length > 2 ? 3 : 4, fg, 1.05);
}

/** The big welcome billboard on the plaza: Taroko's marble gorge painted on it. */
function welcome(c: Ctx, d: Decor): void {
  art(c, d, 'welcome', 108, 56, (c, night) => {
    shadow(c, 0, 0, 50, 3, 0.3, 40);
    for (const px of [-44, 42]) block(c, px, -22, 3, 22, '#5a6470', '#7a8490');
    block(c, -52, -54, 104, 34, '#2a2e34', '#3a3e44', '#1a1e24');
    // The painting: sky, marble walls, the turquoise Liwu river.
    vgrad(c, -50, -52, 100, 30, '#9fd3f0', '#d8eefa');
    poly(c, '#cfcac0', [-50, -22, -50, -44, -36, -38, -24, -46, -10, -32, 0, -22]);
    poly(c, '#b8b4aa', [50, -22, 50, -46, 36, -40, 26, -48, 12, -30, 4, -22]);
    poly(c, '#e8e4da', [-50, -22, -50, -40, -40, -36, -30, -42, -18, -30, -8, -22]);
    poly(c, '#2aa6b0', [-50, -22, -12, -30, 8, -30, 50, -22]);
    poly(c, '#7fd6cf', [-40, -24, -10, -28, 6, -28, 36, -24]);
    for (let i = 0; i < 8; i++) ellipse(c, '#3f7a35', -44 + i * 12, -24 - (i % 2) * 2, 4, 2.4);
    block(c, -46, -34, 48, 10, 'rgba(20,30,40,0.55)', undefined, undefined, 'rgba(0,0,0,0)');
    txt(c, '太魯閣國家公園', -22, -29.2, 5.2, '#ffffff', 900, 'rgba(0,0,0,0.4)');
    txt(c, 'TAROKO NATIONAL PARK · 歡迎您', 0, -22.4, 2.4, '#ffe9b0', 800);
    if (night) {
      for (const lx of [-30, 0, 30]) ellipse(c, 'rgba(255,240,200,0.18)', lx, -40, 22, 16);
      rect(c, '#ffe9b0', -52, -56, 104, 1.2);
    } else rect(c, '#8a9096', -52, -56, 104, 1.2);
  });
}

/** 扁食 (wonton) and 蔥油餅 (scallion pancake) carts with an umbrella and live steam. */
function snackCart(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  art(c, d, 'snackCart', 44, 46, (c, night) => {
    shadow(c, 0, 0, 18, 3, 0.32, 14);
    const umb = v ? '#e2493b' : '#2f6fa8';
    rect(c, '#55606a', -0.6, -44, 1.2, 26);
    c.fillStyle = umb;
    c.beginPath();
    c.moveTo(-21, -34);
    c.quadraticCurveTo(0, -50, 21, -34);
    c.closePath();
    c.fill();
    for (let i = -18; i < 20; i += 6) poly(c, i % 12 ? '#fff6e6' : umb, [i, -34.6, i + 3, -43 - Math.abs(i) * 0.15, i + 6, -34.6]);
    rect(c, shade(umb, -0.3), -21, -34.4, 42, 1);
    block(c, -16, -18, 32, 14, '#c8cdd2', '#e8ecef', '#8a9096');
    rect(c, '#6a7078', -16, -9, 32, 0.8);
    for (const wx of [-10, 10]) {
      ellipse(c, '#1b1d24', wx, -1.5, 3.2, 3.2);
      ellipse(c, '#9aa0a6', wx, -1.5, 1.2, 1.2);
    }
    block(c, -15, -32, 30, 10, v ? '#ffd34e' : '#f4f1ea', '#ffffff', v ? '#c0a030' : '#c8c0b0');
    txt(c, v ? '蔥油餅' : '扁食·餛飩', 0, -27, 5, v ? '#1b1d24' : '#c0392b', 900);
    txt(c, v ? 'Scallion pancake' : 'Hualien wonton', 0, -23.2, 1.9, '#55606a', 700);
    if (v) {
      // Griddle with pancakes and a stack of dough.
      ellipse(c, '#2a2e34', -5, -19, 8, 3.4);
      ellipse(c, '#e8b84a', -7, -19.6, 3, 1.6);
      ellipse(c, '#d8a040', -2, -19.4, 3, 1.6);
      for (let k = 0; k < 4; k++) ellipse(c, '#fff0c8', 9, -18.6 - k * 1.2, 4, 1.2);
    } else {
      // Big steaming pot and bowls.
      block(c, -11, -24, 12, 7, '#a8a8a0', '#d0d0c8', '#6a6a62');
      ellipse(c, '#3a3e44', -5, -24, 6, 1.8);
      for (let k = 0; k < 3; k++) ellipse(c, '#ffffff', 4 + k * 4, -19.2, 2, 1.2);
      ellipse(c, '#e8e4da', 10, -22, 2.4, 1.4);
    }
    if (night) {
      ellipse(c, '#fff3b0', 0, -33, 1.8, 1.8);
      ellipse(c, 'rgba(255,230,140,0.3)', 0, -28, 14, 10);
    }
  });
  if (!v) steam(c, d.x - 5, d.y - 26, t, 3, 0.45);
  else steam(c, d.x - 5, d.y - 22, t, 2, 0.3);
}

/** A concrete utility pole with cross-arms; three wires sag east over `w` px (Taiwan's sky is full of them). */
function cables(c: Ctx, d: Decor): void {
  const w = d.w ?? 128, x = d.x - w / 2, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 2.6, 1);
  rect(c, '#8a8a84', x - 1.2, y - 62, 2.4, 62);
  rect(c, '#a8a8a0', x - 0.4, y - 62, 0.8, 62);
  rect(c, '#5a5a56', x - 6, y - 58, 12, 1.2);
  rect(c, '#5a5a56', x - 5, y - 53, 10, 1.2);
  for (const dx of [-5.4, 0, 5.4]) ellipse(c, '#e8e4da', x + dx, y - 59, 0.9, 1.1);
  if (v % 2) {
    // A transformer drum.
    block(c, x - 3.8, y - 50, 4.6, 7, '#6a7078', '#8a9096', '#4a5058');
    block(c, x + 1, y - 48, 3.6, 5, '#6a7078', '#8a9096', '#4a5058');
  }
  c.strokeStyle = 'rgba(30,30,34,0.75)';
  c.lineWidth = 0.5;
  c.beginPath();
  for (const [dy, sag] of [[-58.5, 7], [-53, 6], [-56, 9]] as const) {
    c.moveTo(x, y + dy);
    c.quadraticCurveTo(x + w / 2, y + dy + sag * 2, x + w, y + dy);
  }
  c.stroke();
  // A bird on the wire, now and then.
  if (rnd(Math.round(d.x), 9) > 0.55) {
    const bx = x + w * 0.5, by = y - 58.5 + 7;
    ellipse(c, '#2a2a30', bx, by - 1.4, 1.6, 1.2);
    ellipse(c, '#2a2a30', bx + 1.2, by - 2.6, 0.9, 0.8);
  }
}

/** Little shed with a corrugated-iron roof, a water tank and odds and ends (very Taiwanese backstreet). */
function tinRoof(c: Ctx, d: Decor): void {
  const v = d.variant ?? 0;
  art(c, d, 'tinRoof', 40, 34, (c, night) => {
    const wallCol = ['#d8d0c0', '#b8c8c0', '#e0d4b8'][v % 3];
    const roofCol = ['#6a7078', '#8a5a44', '#4a6a7a'][v % 3];
    shadow(c, 0, 0, 18, 3, 0.3, 18);
    block(c, -16, -18, 32, 18, wallCol, shade(wallCol, 0.2), shade(wallCol, -0.3));
    for (let i = -14; i < 16; i += 4) rect(c, 'rgba(0,0,0,0.08)', i, -17, 0.6, 16);
    // Door and a small window.
    block(c, -6, -14, 7, 14, '#5a4a3a', '#7a6a58', '#3a2a1a');
    pane(c, 5, -13, 7, 5, night, v, '#8a8a84');
    // Corrugated roof, slanted.
    poly(c, shade(roofCol, -0.3), [-19, -18, 19, -18, 19, -22, -19, -26]);
    poly(c, roofCol, [-19, -19, 19, -19, 19, -23, -19, -27]);
    for (let i = -17; i < 19; i += 2.2) rect(c, i % 4.4 ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.18)', i, -27 + (i + 19) * 0.105, 1, 8);
    rect(c, 'rgba(120,70,40,0.35)', -8, -24.2, 10, 1.6);
    // Water tank and a bucket.
    block(c, 6, -34, 9, 11, '#c8cdd2', '#e8ecef', '#8a9096');
    ellipse(c, '#e8ecef', 10.5, -34, 4.5, 1.6);
    rect(c, '#6a7078', 8, -23, 1, 4);
    block(c, -15, -5, 5, 5, '#2f6fa8', '#4a8ac8');
    if (v % 3 === 1) {
      // Stacked crates.
      block(c, 11, -8, 7, 4, '#e2493b', '#ff6a5a');
      block(c, 11, -4, 7, 4, '#3a8fd9', '#5aa8f0');
    }
  });
}

/** Two people chatting on a bench; their legs sit behind the seat so they read as seated. */
function sitters(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const w = 30;
  shadow(c, x, y, w / 2 + 2, 2.6, 0.3);
  // Backrest.
  block(c, x - w / 2, y - 17, w, 4, '#8a5a34', '#a8784a', '#6a4428');
  const n = v % 3 === 2 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    const look = CROWD[(v + i * 3) % CROWD.length];
    const cx = n === 1 ? x + 2 : x - 7 + i * 14;
    const seed = rnd(v, i);
    drawCharacter(c, look, cx, y - 3, { facing: 'down', phase: 0, moving: false, blink: (t * 0.7 + seed * 4) % 4 < 0.12, breath: (t * 0.5 + seed) % 1 });
  }
  // Seat (covers the legs) and legs of the bench.
  block(c, x - w / 2, y - 10, w, 5, '#a8784a', '#c8986a', '#6a4428');
  for (const lx of [x - w / 2 + 2, x + w / 2 - 4]) block(c, lx, y - 5, 2.4, 5, '#5a4a3a');
  if (v % 4 === 1) {
    // A bubble-tea cup in the first person's hand.
    block(c, x - 1.5, y - 12, 3, 4.4, '#f4e6c0', '#ffffff');
    rect(c, '#1b1d24', x - 0.2, y - 15, 0.6, 3.4);
  }
}

/** A Taiwan dog asleep in the shade (ear twitches now and then). */
function dog(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const col = v ? '#3a2a20' : '#c89050', dark = shade(col, -0.3);
  shadow(c, x, y, 8, 2, 0.25);
  const breath = Math.sin(t * 2.2 + x) * 0.3;
  ellipse(c, col, x, y - 3, 7.5, 3.6 + breath);
  ellipse(c, dark, x + 5, y - 2, 3, 1.6);
  ellipse(c, col, x - 5, y - 4.5, 3.6, 3);
  ellipse(c, dark, x - 8, y - 4, 1.6, 1);
  const twitch = Math.sin(t * 1.3 + x) > 0.96 ? 1 : 0;
  poly(c, dark, [x - 6, y - 7, x - 4, y - 10 - twitch, x - 3, y - 7]);
  poly(c, dark, [x - 8.5, y - 6, x - 7.5, y - 9 - twitch, x - 6, y - 6.5]);
  rect(c, '#1b1d24', x - 8.8, y - 4.6, 1, 0.6);
  // Curled tail.
  line(c, col, 1.4, [x + 6, y - 4, x + 9, y - 7, x + 7, y - 9]);
}

/** A manhole cover, flush with the ground. */
function manhole(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  ellipse(c, '#5a5a56', x, y, 7, 3.2);
  ellipse(c, '#8a8a84', x, y - 0.4, 6.2, 2.7);
  for (let i = -4; i <= 4; i += 2) rect(c, '#6a6a64', x + i - 0.4, y - 2.2 + Math.abs(i) * 0.3, 0.8, 4 - Math.abs(i) * 0.6);
  rect(c, '#6a6a64', x - 5, y - 0.8, 10, 0.5);
}

/** A rain puddle reflecting the sky, with a soft wobble. */
function puddle(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 26;
  const night = isNight();
  const wob = Math.sin(t * 0.8 + x) * 0.6;
  ellipse(c, 'rgba(40,50,60,0.22)', x, y, w / 2 + 1, w / 6 + 0.8);
  ellipse(c, night ? 'rgba(90,110,140,0.55)' : 'rgba(150,190,220,0.6)', x + wob, y - 0.3, w / 2, w / 6);
  ellipse(c, night ? 'rgba(255,230,170,0.25)' : 'rgba(255,255,255,0.45)', x - w * 0.18 + wob, y - w / 10, w * 0.16, 1);
  ellipse(c, night ? 'rgba(255,230,170,0.18)' : 'rgba(255,255,255,0.3)', x + w * 0.2, y + 0.6, w * 0.1, 0.6);
}

/** Taiwan's blue farm pickup (小發財), loaded with vegetable crates. */
function truck(c: Ctx, d: Decor): void {
  art(c, d, 'truck', 48, 30, (c, night) => {
    shadow(c, 0, 0, 22, 3.2, 0.32, 12);
    const col = '#2a5aa8';
    block(c, -22, -12, 44, 10, col, shade(col, 0.25), shade(col, -0.3));
    block(c, 8, -21, 14, 10, col, shade(col, 0.25));
    vgrad(c, 9.5, -19.6, 11, 5.4, '#bfe6f6', '#7ab0c8');
    rect(c, 'rgba(255,255,255,0.35)', 10, -19, 1.4, 4);
    // Cargo bed with crates and a tarp.
    block(c, -21, -16, 28, 5, shade(col, -0.2), shade(col, 0.05));
    for (let i = 0; i < 4; i++) {
      block(c, -19 + i * 6.5, -21 + (i % 2) * 1.5, 6, 5, i % 2 ? '#c8a060' : '#57c278', i % 2 ? '#e0c080' : '#7ad898');
      for (let k = 0; k < 3; k++) ellipse(c, i % 2 ? '#e8473a' : '#2a8a4a', -17 + i * 6.5 + k * 2, -22 + (i % 2) * 1.5, 1, 0.8);
    }
    rect(c, '#2a2a30', -22, -7, 44, 0.7);
    for (const wx of [-14, 13]) {
      ellipse(c, '#1b1d24', wx, -2, 3.4, 3.4);
      ellipse(c, '#9aa0a6', wx, -2, 1.3, 1.3);
    }
    rect(c, night ? '#fff3c0' : '#e8e4da', 20.6, -10, 1.4, 1.8);
    rect(c, '#c8202a', -22, -10, 1, 1.8);
  });
}

const NEONS: [string, string][] = [['美食', '#ff4a6a'], ['冰品', '#3ad9ff'], ['名產', '#ffd34e'], ['炸物', '#ff9f43'], ['手搖飲', '#7ad0e0'], ['海鮮', '#4affc0']];

/** A vertical neon shop sign, humming in colour at night. */
function neon(c: Ctx, d: Decor, t: number): void {
  const [text, col] = NEONS[(d.variant ?? 0) % NEONS.length];
  const x = d.x, y = d.y, night = isNight();
  const h = text.length * 9 + 8;
  const top = y - 58;
  rect(c, '#3a3e44', x - 4, top + 2, 8, 1);
  rect(c, '#3a3e44', x - 4, top + h - 3, 8, 1);
  block(c, x - 6, top, 12, h, '#1b1d24', '#3a3e44', '#0e1014');
  if (night) {
    const flick = Math.sin(t * 9 + x) > -0.92 ? 1 : 0.5;
    c.save();
    c.shadowColor = col;
    c.shadowBlur = 6 * flick;
    vtxt(c, text, x, top + 7, 7, col, 1.25);
    c.restore();
    ellipse(c, `rgba(${col === '#ff4a6a' ? '255,74,106' : col === '#ffd34e' ? '255,211,78' : '80,220,255'},${0.14 * flick})`, x, top + h / 2, 12, h / 2 + 4);
  } else vtxt(c, text, x, top + 7, 7, shade(col, -0.25), 1.25);
}

/** A string of bare bulbs across a market lane (lit at night, with a gentle twinkle). */
function bulbs(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 128, x0 = d.x - w / 2, y0 = d.y - 58, night = isNight();
  const sag = (k: number) => y0 + Math.sin(k * Math.PI) * 8;
  c.strokeStyle = 'rgba(30,30,34,0.8)';
  c.lineWidth = 0.6;
  c.beginPath();
  c.moveTo(x0, y0);
  c.quadraticCurveTo(x0 + w / 2, y0 + 16, x0 + w, y0);
  c.stroke();
  const n = Math.max(3, Math.round(w / 14));
  for (let i = 0; i <= n; i++) {
    const k = i / n, bx = x0 + k * w, by = sag(k) + 3;
    rect(c, '#2a2a30', bx - 0.5, by - 3, 1, 3);
    const on = night && Math.sin(t * 2.5 + i * 1.3 + d.x) > -0.85;
    ellipse(c, on ? '#fff3b0' : '#d8d4c8', bx, by + 1, 1.6, 2);
    if (on) ellipse(c, 'rgba(255,220,140,0.22)', bx, by + 1, 6, 6);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  ...STALLS,
  hc_shuttle: shuttleStop,
  hc_mural: mural,
  hc_shrine: shrine,
  hc_marketMap: marketMap,
  hc_creative: (c, r) =>
    artObj(c, r, 'creativeBoard', 68, 36, (c) => {
      shadow(c, 0, 0, 30, 2.4, 0.3);
      for (const x of [-26, 24]) block(c, x, -12, 2.4, 12, '#3a3e44');
      block(c, -30, -30, 60, 19, '#2a2a2e', '#3a3a40');
      txt(c, '花蓮文化創意產業園區', 0, -24.6, 4.6, '#f4e6c0', 900);
      txt(c, 'HUALIEN CULTURAL & CREATIVE PARK', 0, -19.6, 2.2, '#d8c8a8', 700);
      txt(c, '1913 · 舊酒廠 Old Winery', 0, -15.4, 2.4, '#ffd34e', 800);
    }),
  hc_pineGarden: pineStone,
  hc_riverMouth: (c, r) => binoculars(c, r, '美崙溪口', 'MEILUN RIVER MOUTH'),
  hc_seafront: (c, r) => binoculars(c, r, '北濱公園', 'BEIBIN PARK'),
  hc_bikePath: (c, r) =>
    artObj(c, r, 'bikeBoard', 36, 44, (c) => {
      shadow(c, 0, 0, 12, 2, 0.3);
      for (const x of [-10, 8]) rect(c, '#55606a', x, -20, 2, 20);
      block(c, -14, -40, 28, 22, '#2f6fa8', '#4a8ac8', '#1f4a7a');
      txt(c, '花蓮海岸', 0, -35.6, 4, '#ffffff', 900);
      txt(c, '自行車道', 0, -30.6, 4, '#ffffff', 900);
      line(c, '#ffd34e', 0.8, [-11, -23, -6, -26, -1, -22, 4, -26, 10, -23]);
      ellipse(c, '#e8473a', -11, -23, 1, 1);
    }),
  hc_container: cafeMenu,
  hc_photo: photoFrame,
  hc_temple: offeringBox,
  hc_buddha88: buddhaBoard,
  hc_paddy: paddySign,
  hc_store: () => undefined,
};

registerPropDrawers({
  objects: OBJECTS,
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'hc_station':
        landmark(c, d, 'station', station);
        return true;
      case 'hc_train':
        art(c, d, 'train', 560, 40, train);
        return true;
      case 'hc_amisPillar':
        amisPillar(c, d);
        return true;
      case 'hc_taxi':
        taxi(c, d);
        return true;
      case 'hc_taxiSign':
        taxiSign(c, d);
        return true;
      case 'hc_bus':
        bus(c, d);
        return true;
      case 'hc_warehouse':
        warehouse(c, d, 0);
        return true;
      case 'hc_warehouse2':
        warehouse(c, d, 1);
        return true;
      case 'hc_warehouse3':
        warehouse(c, d, 2);
        return true;
      case 'hc_artCube':
        artCube(c, d);
        return true;
      case 'hc_pineGarden':
        landmark(c, d, 'pine', pineGarden);
        return true;
      case 'hc_ryukyuPine':
        ryukyuPine(c, d);
        return true;
      case 'hc_shops':
        shops(c, d);
        return true;
      case 'hc_gate':
        gate(c, d, t);
        return true;
      case 'hc_gatePost':
        return true;
      case 'hc_alleySign':
        alleySign(c, d);
        return true;
      case 'hc_stage':
        stage(c, d, t);
        return true;
      case 'hc_carvedPost':
        carvedPost(c, d);
        return true;
      case 'hc_stallDeco':
        decoStall(c, d, t);
        return true;
      case 'hc_crowd':
        crowd(c, d, t);
        return true;
      case 'hc_tourGroup':
        tourGroup(c, d, t);
        return true;
      case 'hc_hotel':
        landmark(c, d, 'hotel', hotel);
        return true;
      case 'hc_palm':
        palm(c, d, t);
        return true;
      case 'hc_betel':
        betel(c, d, t);
        return true;
      case 'hc_bench':
        bench(c, d);
        return true;
      case 'hc_railing':
        railing(c, d);
        return true;
      case 'hc_bikeSign':
        bikeSign(c, d);
        return true;
      case 'hc_container':
        landmark(c, d, 'container', containerCafe);
        return true;
      case 'hc_umbrellas':
        umbrellas(c, d);
        return true;
      case 'hc_temple':
        landmark(c, d, 'temple', templeHall);
        return true;
      case 'hc_buddhas':
        buddhas(c, d);
        return true;
      case 'hc_toro':
        toro(c, d);
        return true;
      case 'hc_basin':
        basin(c, d);
        return true;
      case 'hc_scarecrow':
        scarecrow(c, d, t);
        return true;
      case 'hc_egret':
        egret(c, d, t);
        return true;
      case 'hc_house':
        house(c, d);
        return true;
      case 'hc_store':
        store(c, d);
        return true;
      case 'hc_waterTower':
        waterTower(c, d);
        return true;
      case 'hc_traveler':
        traveler(c, d, t);
        return true;
      case 'hc_queue':
        queue(c, d, t);
        return true;
      case 'hc_flamboyant':
        flamboyant(c, d, t);
        return true;
      case 'hc_flowerPot':
        flowerPot(c, d);
        return true;
      case 'hc_banner':
        banner(c, d, t);
        return true;
      case 'hc_welcome':
        welcome(c, d);
        return true;
      case 'hc_snackCart':
        snackCart(c, d, t);
        return true;
      case 'hc_cables':
        cables(c, d);
        return true;
      case 'hc_tinRoof':
        tinRoof(c, d);
        return true;
      case 'hc_sitters':
        sitters(c, d, t);
        return true;
      case 'hc_dog':
        dog(c, d, t);
        return true;
      case 'hc_manhole':
        manhole(c, d);
        return true;
      case 'hc_puddle':
        puddle(c, d, t);
        return true;
      case 'hc_truck':
        truck(c, d);
        return true;
      case 'hc_neon':
        neon(c, d, t);
        return true;
      case 'hc_bulbs':
        bulbs(c, d, t);
        return true;
      default:
        return false;
    }
  },
  animatedObjects: [...Object.keys(STALLS), 'hc_shrine'],
  animatedDecor: ['hc_stallDeco', 'hc_gate', 'hc_stage', 'hc_crowd', 'hc_tourGroup', 'hc_palm', 'hc_betel', 'hc_scarecrow', 'hc_egret', 'hc_traveler', 'hc_queue', 'hc_flamboyant', 'hc_banner', 'hc_snackCart', 'hc_sitters', 'hc_dog', 'hc_puddle', 'hc_neon', 'hc_bulbs'],
});
